<template>
  <div>
    <!-- Button to open popup -->
    <button
      @click="showPopup = true"
      class="w-full flex flex-col items-center justify-center px-0 py-2 text-gray-700 hover:bg-gray-100 border-b border-gray-200 transition-colors relative"
      :class="isSidebarCollapsed ? '' : 'flex-row items-center px-4 py-2 gap-2'"
      title="Automated Projections Profiles"
    >
      <template v-if="isSidebarCollapsed">
        <i class="fas fa-chart-line text-xl mb-1"></i>
        <div class="flex items-center text-[8px] text-gray-400 leading-tight">
          <span>Auto</span>
        </div>
      </template>
      <template v-else>
        <i class="fas fa-chart-line text-sm"></i>
        <span class="ml-2 font-medium text-sm">Projections Profiles</span>
      </template>
    </button>

    <!-- Popup Overlay -->
    <div
      v-if="showPopup"
      class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2"
      @click="closePopup"
    >
      <!-- Popup Content -->
      <div
        class="bg-white rounded-lg shadow-xl w-full max-w-[95vw] h-[95vh] flex overflow-y-hidden"
        @click.stop
      >
        <!-- Left Side Menu -->
        <div class="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
          <!-- Header -->
          <div class="p-4 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-800">Projections Settings</h2>
            <p class="text-sm text-gray-600 mt-1">Manage automated projections</p>
          </div>

          <!-- Menu Items -->
          <nav class="flex-1 p-4 space-y-2">
            <button
              v-for="item in menuItems"
              :key="item.id"
              @click="activeTab = item.id"
              class="w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors"
              :class="activeTab === item.id 
                ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                : 'text-gray-700 hover:bg-gray-100'"
            >
              <i :class="item.icon" class="w-5 h-5 mr-3"></i>
              <span class="font-medium">{{ item.name }}</span>
              <span v-if="item.count" class="ml-auto bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                {{ item.count }}
              </span>
            </button>
          </nav>


        </div>

        <!-- Right Content Area -->
        <div 
        v-if="activeTab !== 'player_labels'"
        class="flex flex-col w-full flex-grow overflow-y-auto">
          <!-- Content Header -->
          <div class="p-6 border-b border-gray-200">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-xl font-semibold text-gray-800">
                  {{ activeTabData?.name || 'Unknown Tab' }}
                </h3>
                <p class="text-gray-600 mt-1">{{ activeTabData?.description || 'No description available' }}</p>
              </div>
              <div class="flex items-center space-x-2">
                <button
                  @click="refreshProfiles"
                  class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <i class="fas fa-sync-alt mr-2"></i>
                  Refresh
                </button>
                <button
                  @click="addNewProfile"
                  class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <i class="fas fa-plus mr-2"></i>
                  Add New
                </button>
                <button
                  @click="showPopup = false"
                  class="px-3 py-2 text-gray-500 hover:text-gray-700 transition-colors"
                  title="Close"
                >
                  <i class="fas fa-times text-lg"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Content Body -->
          <div class="flex-1 flex">
            <!-- Players Profiles -->
            <div v-if="activeTab === 'players'" class="flex w-full">
              <!-- Left Column - Search and Results -->
              <div class="w-80 border-r border-gray-200 bg-gray-50 p-4 flex flex-col">
                <!-- Search Section -->
                <div class="bg-white border border-gray-200 rounded-lg p-4 flex-1 flex flex-col">
                  <div class="flex items-center space-x-2 mb-3">
                    <div class="flex-1">
                      <label class="block text-sm font-medium text-gray-700 mb-2">Search Players</label>
                      <div class="relative">
                        <input
                          v-model="playerSearch"
                          @input="searchPlayers"
                          type="text"
                          placeholder="Search for players..."
                          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                        <div v-if="isSearching" class="absolute right-3 top-2">
                          <i class="fas fa-spinner fa-spin text-gray-400 text-sm"></i>
                        </div>
                      </div>
                    </div>
                    <button
                      @click="clearPlayerSearch"
                      class="px-2 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                      title="Clear search"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                  
                  <!-- Search Results -->
                  <div v-if="searchResults.length > 0" class="flex-1 overflow-y-auto">
                    <h4 class="text-sm font-medium text-gray-700 mb-3">Results ({{ searchResults.length }})</h4>
                    <div class="space-y-2 px-2">
                      <div
                        v-for="(player, index) in searchResults"
                        :key="player.id"
                        class="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:bg-gray-100 transition-colors cursor-pointer"
                        :class="selectedPlayer?.id === player.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''"
                        @click="openPlayerProfile(player)"
                      >
                        <div class="flex items-center justify-between">
                          <div class="flex-1">
                            <h5 class="font-medium text-gray-800 text-sm">{{ player.player_name }}</h5>
                            <div class="text-xs text-gray-600 mt-1">
                              <p v-if="player.team">{{ player.team }}</p>
                              <p v-if="player.position">{{ player.position }}</p>
                              <p v-if="player.nationality">{{ player.nationality }}</p>
                              <p v-if="player.age">Age: {{ player.age }}</p>
                              <p v-if="player.player_id">Player ID: {{ player.player_id }}</p>
                            </div>
                          </div>
                          <div class="text-gray-400 ml-2">
                            <i class="fas fa-chevron-right text-xs"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div v-else-if="playerSearch && !isSearching" class="flex-1 flex items-center justify-center">
                    <div class="text-center text-gray-500 text-sm">
                      No players found
                    </div>
                  </div>

                  <div v-else class="flex-1 flex items-center justify-center">
                    <div class="text-center text-gray-500 text-sm">
                      <i class="fas fa-search text-2xl mb-2"></i>
                      <p>Search for players to get started</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Column - Player Profile Display -->
              <div class="flex-1 p-6 flex flex-col min-h-0">
                <!-- Direct Player Profile Section -->
                <div v-if="selectedPlayer" class="flex-1 flex flex-col space-y-4 min-h-0">
                  <!-- Player Navigation Header -->
                  <div class="bg-white border border-gray-200 rounded-lg p-4">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center space-x-4">
                        <h3 class="text-lg font-semibold text-gray-800">
                          {{ selectedPlayer.player_name }}
                        </h3>
                        <div class="text-sm text-gray-500 space-y-1">
                          <div v-if="selectedPlayer.team || selectedPlayer.position || selectedPlayer.nationality">
                            <span v-if="selectedPlayer.team">{{ selectedPlayer.team }}</span>
                            <span v-if="selectedPlayer.position"> • {{ selectedPlayer.position }}</span>
                            <span v-if="selectedPlayer.nationality"> • {{ selectedPlayer.nationality }}</span>
                          </div>
                          <div>
                            {{ currentPlayerIndex + 1 }} of {{ searchResults.length }} results
                          </div>
                        </div>
                        <div v-if="isLoadingPlayer" class="flex items-center text-blue-600">
                          <i class="fas fa-spinner fa-spin mr-2"></i>
                          <span class="text-sm">Loading...</span>
                        </div>
                      </div>
                      <div class="flex items-center space-x-2">
                        <button
                          @click="previousPlayer"
                          :disabled="currentPlayerIndex === 0 || isLoadingPlayer"
                          class="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Previous player"
                        >
                          <i class="fas fa-chevron-left"></i>
                        </button>
                        <button
                          @click="nextPlayer"
                          :disabled="currentPlayerIndex === searchResults.length - 1 || isLoadingPlayer"
                          class="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Next player"
                        >
                          <i class="fas fa-chevron-right"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Loading State -->
                  <div v-if="isLoadingPlayer" class="flex items-center justify-center py-12">
                    <div class="text-center text-gray-500">
                      <i class="fas fa-spinner fa-spin text-4xl mb-4"></i>
                      <p class="text-lg">Loading player details...</p>
                    </div>
                  </div>

                  <!-- Player Profile Component -->
                  <PlayerProfile v-else :content="{
                    ...selectedPlayer,
                    name: selectedPlayer.player_name,
                    team: selectedPlayer.team || 'Team information not available',
                    birthDate: selectedPlayer.birthdate || 'Birth date not available',
                    height: selectedPlayer.height_cm ? `${selectedPlayer.height_cm}cm` : 'Height not available',
                    nationality: selectedPlayer.nationality || 'Nationality not available',
                    age: selectedPlayer.age || 'Age not available',
                    roster: selectedPlayer.roster || [],
                    // Additional fields for the PlayerProfile component
                    pts: selectedPlayer.pts || 0,
                    reb: selectedPlayer.reb || 0,
                    ast: selectedPlayer.ast || 0,
                    stl: selectedPlayer.stl || 0,
                    blk: selectedPlayer.blk || 0,
                    // Team information
                    team_id: selectedPlayer.team_id,
                    position: selectedPlayer.position || 'Position not specified',
                    // Player links
                    player_link: selectedPlayer.player_link,
                    p_player_id: selectedPlayer.p_player_id,
                    original_id: selectedPlayer.original_id,
                    // Use roster teams for active teams
                    activeTeams: selectedPlayer.roster?.map(rosterItem => rosterItem.team?.team_name).filter(Boolean) || [selectedPlayer.team].filter(Boolean)
                  }" />
                  
                                     <!-- Post Button -->
                   <div class="hidden flex justify-end flex-shrink-0">
                     <button
                       @click="postPlayerProfile"
                       :disabled="isLoadingPlayer"
                       class="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                       <i class="fas fa-paper-plane mr-2"></i>
                       Post Profile
                     </button>
                   </div>
                </div>

                <!-- Empty State -->
                <div v-if="!selectedPlayer" class="flex items-center justify-center h-full">
                  <div class="text-center text-gray-500">
                    <i class="fas fa-search text-4xl mb-4"></i>
                    <p class="text-lg">Search for players to view their profiles</p>
                    <p class="text-sm">Click on any player from the search results to view their profile</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Leagues Tab -->
            <LeagueManagement v-if="activeTab === 'leagues'" />

            <!-- Teams Tab -->
            <div v-if="activeTab === 'teams'" class="flex w-full">
              <!-- Left Column - Teams Search and Results -->
              <div class="w-80 border-r border-gray-200 bg-gray-50 p-4 flex flex-col">
                <!-- Teams Section -->
                <div class="bg-white border border-gray-200 rounded-lg p-4 flex-1 flex flex-col">
                  <div class="flex items-center space-x-2 mb-3">
                    <div class="flex-1">
                      <label class="block text-sm font-medium text-gray-700 mb-2">Search Teams</label>
                      <div class="relative">
                        <input
                          v-model="teamSearch"
                          @input="searchTeams"
                          type="text"
                          placeholder="Search for teams..."
                          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                        <div v-if="isSearchingTeams" class="absolute right-3 top-2">
                          <i class="fas fa-spinner fa-spin text-gray-400 text-sm"></i>
                        </div>
                      </div>
                    </div>
                    <button
                      @click="clearTeamSearch"
                      class="px-2 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                      title="Clear search"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                  
                  <!-- Teams Results -->
                  <div v-if="teamResults.length > 0" class="flex-1 overflow-y-auto">
                    <h4 class="text-sm font-medium text-gray-700 mb-3">Results ({{ teamResults.length }})</h4>
                    <div class="space-y-2">
                      <div
                        v-for="(team, index) in teamResults"
                        :key="team.id"
                        class="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:bg-gray-100 transition-colors cursor-pointer"
                        :class="selectedTeam?.id === team.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''"
                        @click="openTeamDetails(team)"
                      >
                        <div class="flex items-center justify-between">
                          <div class="flex-1">
                            <h5 class="font-medium text-gray-800 text-sm">{{ team.team_name }} {{ team.id }}</h5>
                            <div class="text-xs text-gray-600 mt-1">
                              <p v-if="team.league">{{ team.league }}</p>
                              <p v-if="team.region">{{ team.region }}</p>
                            </div>
                          </div>
                          <div class="text-gray-400 ml-2">
                            <i class="fas fa-chevron-right text-xs"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div v-else-if="teamSearch && !isSearchingTeams" class="flex-1 flex items-center justify-center">
                    <div class="text-center text-gray-500 text-sm">
                      No teams found
                    </div>
                  </div>

                  <div v-else class="flex-1 flex items-center justify-center">
                    <div class="text-center text-gray-500 text-sm">
                      <i class="fas fa-users text-2xl mb-2"></i>
                      <p>Search for teams to get started</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Column - Team Details Display -->
              <div class="flex-1 p-6 flex flex-col min-h-0">
                <!-- Team Details Section -->
                <div v-if="selectedTeam" class="flex-1 flex flex-col space-y-4 min-h-0">
                  <!-- Team Navigation Header -->
                  <div class="bg-white border border-gray-200 rounded-lg p-4">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center space-x-4">
                        <h3 class="text-lg font-semibold text-gray-800">
                          {{ selectedTeam.team_name }}
                        </h3>
                        <div class="text-sm text-gray-500 space-y-1">
                          <div v-if="selectedTeam.league || selectedTeam.region">
                            <span v-if="selectedTeam.league">{{ selectedTeam.league }}</span>
                            <span v-if="selectedTeam.region"> • {{ selectedTeam.region }}</span>
                          </div>
                          <div>
                            {{ currentTeamIndex + 1 }} of {{ teamResults.length }} results
                          </div>
                        </div>
                        <div v-if="isLoadingTeam" class="flex items-center text-blue-600">
                          <i class="fas fa-spinner fa-spin mr-2"></i>
                          <span class="text-sm">Loading...</span>
                        </div>
                      </div>
                      <div class="flex items-center space-x-2">
                        <button
                          @click="previousTeam"
                          :disabled="currentTeamIndex === 0 || isLoadingTeam"
                          class="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Previous team"
                        >
                          <i class="fas fa-chevron-left"></i>
                        </button>
                        <button
                          @click="nextTeam"
                          :disabled="currentTeamIndex === teamResults.length - 1 || isLoadingTeam"
                          class="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Next team"
                        >
                          <i class="fas fa-chevron-right"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Loading State -->
                  <div v-if="isLoadingTeam" class="flex items-center justify-center py-12">
                    <div class="text-center text-gray-500">
                      <i class="fas fa-spinner fa-spin text-4xl mb-4"></i>
                      <p class="text-lg">Loading team details...</p>
                    </div>
                  </div>

                  <!-- Team Details Content -->
                  <div v-else class="bg-white border border-gray-200 rounded-lg p-6 flex-1 flex flex-col min-h-0">
                    <div class="flex-1 flex flex-col min-h-0">
                      <div>
                        <h4 class="text-lg font-medium text-gray-800 mb-2">Team Information</h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
                            <input
                              v-model="selectedTeam.team_name"
                              type="text"
                              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">RealGM Team Mapping</label>
                            <button @click.stop.prevent="openPBTeamModal" type="button" class="w-full px-3 py-2 border rounded-lg text-left flex items-center justify-between hover:bg-gray-50">
                              <span>{{ selectedTeam.team_name }} → <strong>{{ selectedTeam.realgm_team_name || 'Select RealGM team' }}</strong></span>
                              <i class="fas fa-chevron-right text-gray-400"></i>
                            </button>
                          </div>
                          
                          <!-- <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">League</label>
                            <input
                              v-model="selectedTeam.league"
                              type="text"
                              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div> -->
                        </div>
                        <!-- <div class="mt-4">
                          <label class="block text-sm font-medium text-gray-700 mb-1">Region</label>
                          <input
                            v-model="selectedTeam.region"
                            type="text"
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div> -->
                      </div>

                      <!-- Team Roster and Leagues Side by Side -->
                      <div class="flex-1 flex flex-col min-h-0">
                        <div class="flex items-center justify-between mb-4">
                          <h4 class="text-lg font-medium text-gray-800">Team Management</h4>
                        </div>
                        
                        <!-- Three Column Layout: Roster, Leagues, and Projections -->
                        <div class="flex-1 flex gap-4 min-h-0">
                          <!-- Left Column - Team Roster -->
                          <div class="flex-1 flex flex-col min-h-0">
                            <div class="flex items-center justify-between mb-4">
                              <h5 class="text-md font-medium text-gray-700">Team Roster ({{ selectedTeam.roster?.length || 0 }})</h5>
                              <button
                                @click="fetchLastThreeGamesForPlayerAllSelectedTeamPlayers()"
                                :disabled="isLoadingPlayerGames"
                                class="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                              >
                                <i v-if="isLoadingPlayerGames" class="fas fa-spinner fa-spin"></i>
                                {{ isLoadingPlayerGames ? 'Loading...' : 'Auto Update Statuses' }}
                              </button>
                            </div>
                            
                            <!-- Two Column Layout: Search and Active Players -->
                            <div class="flex-1 flex gap-4 min-h-0">
                              <!-- Left Column - Player Search (Small) -->
                              <div class="w-48 bg-white rounded-xs shadow border border-gray-100 p-4 flex flex-col min-h-0">
                                <div class="flex items-center mb-3">
                                  <i class="fas fa-search text-blue-500 mr-2"></i>
                                  <span class="font-semibold text-gray-700 text-sm">Search Players</span>
                                </div>
                                
                                <!-- Search Input -->
                                <div class="mb-4">
                                  <input
                                    v-model="playerSearchQuery"
                                    @input="searchPlayersForRoster"
                                    type="text"
                                    placeholder="Search players..."
                                    class="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                                  />
                                </div>
                                
                                <!-- Search Results -->
                                <div class="flex-1 overflow-auto min-h-0">
                                  <div v-if="isSearchingPlayers" class="text-center text-gray-500 py-2">
                                    <i class="fas fa-spinner fa-spin mr-1 text-xs"></i>
                                    <span class="text-xs">Searching...</span>
                                  </div>
                                  
                                  <div v-else-if="playerSearchResults.length > 0" class="space-y-1">
                                    <div class="text-xs font-medium text-gray-700 mb-1">
                                      Found {{ playerSearchResults.length }} players
                                    </div>
                                    <div
                                      v-for="player in playerSearchResults"
                                      :key="player.id"
                                      class="bg-gray-50 border border-gray-200 rounded p-1 hover:bg-gray-100 transition-colors"
                                    >
                                      <div class="flex items-center justify-between">
                                        <div class="flex-1 min-w-0">
                                          <div class="flex items-center space-x-1">
                                            <i class="fas fa-user text-gray-400 text-xs"></i>
                                            <span class="text-xs font-medium text-gray-800 truncate">{{ player.player_name || 'Unknown Player' }}</span>
                                          </div>
                                          <div class="text-xs text-gray-500">
                                            <span v-if="player.position">{{ player.position }}</span>
                                            <span v-if="player.position && player.nationality"> • </span>
                                            <span v-if="player.nationality">{{ player.nationality }}</span>
                                          </div>
                                        </div>
                                        <button
                                          @click="addPlayerToRoster(player)"
                                          class="ml-1 px-1 py-0.5 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 transition-colors flex-shrink-0"
                                          title="Add to roster"
                                        >
                                          <i class="fas fa-plus text-xs"></i>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div v-else-if="playerSearchQuery && !isSearchingPlayers" class="text-center text-gray-500 py-2">
                                    <i class="fas fa-search text-lg mb-1"></i>
                                    <p class="text-xs">No players found</p>
                                  </div>
                                  
                                  <div v-else class="text-center text-gray-500 py-2">
                                    <i class="fas fa-search text-lg mb-1"></i>
                                    <p class="text-xs">Search for players</p>
                                  </div>
                                </div>
                              </div>

                              <!-- Right Column - Active Players -->
                              <div class="flex-1 bg-white rounded-xs shadow border border-gray-100 p-4 flex flex-col min-h-0">
                                <div class="flex items-center mb-2">
                                  <i class="fas fa-users text-blue-500 mr-2"></i>
                                  <span class="font-semibold text-gray-700 text-sm">Current Team Players</span>
                                </div>
                                
                                Column Headers
                                <div v-if="selectedTeam.roster && selectedTeam.roster.length > 0" class="hidden grid grid-cols-[2fr_80px_1fr_auto] gap-2 items-center px-2 py-2 mb-1 bg-gray-100 rounded border-b border-gray-200 sticky top-0 z-10">
                                  <div class="text-xs font-semibold text-gray-700">Player</div>
                                  <div class="text-xs font-semibold text-gray-700">Avg Time</div>
                                  <div class="flex items-center space-x-1">
                                    <div class="w-[35px] flex justify-start">
                                      <span class="text-[9px] font-semibold text-gray-600">L1</span>
                                    </div>
                                    <div class="w-[35px] flex justify-start">
                                      <span class="text-[9px] font-semibold text-gray-600">L2</span>
                                    </div>
                                    <div class="w-[35px] flex justify-start">
                                      <span class="text-[9px] font-semibold text-gray-600">L3</span>
                                    </div>
                                  </div>
                                  <div class="text-xs font-semibold text-gray-700 text-right">Actions</div>
                                </div>
                                
                                <ul v-if="selectedTeam.roster && selectedTeam.roster.length > 0" class="space-y-1 flex-1 overflow-auto min-h-0">
                                  <li 
                                    v-for="rosterItem in selectedTeam.roster" 
                                    :key="rosterItem.id" 
                                    class="bg-gray-50 rounded transition-colors"
                                  >
                                    <!-- Player Item -->
                                    <div 
                                      class="grid grid-cols-[2fr_80px_1fr_auto] gap-2 items-center px-2 py-2 hover:bg-gray-100 transition-colors cursor-pointer"
                                      @contextmenu.prevent="handleRightClick($event, rosterItem)"
                                      data-no-global-context-menu
                                    >
                                      <!-- Column 1: Player Info -->
                                      <div class="flex items-center space-x-2 min-w-0">
                                        <i class="fas fa-user text-gray-400 flex-shrink-0"></i>
                                        <div class="min-w-0 flex-1">
                                          <div class="font-medium text-sm truncate">
                                            {{ rosterItem.player?.player_name || 'Unknown Player' }}
                                          </div>
                                          <div 
                                            class="text-xs text-white bg-green-500 rounded-full px-2 py-0.5 cursor-pointer hover:bg-green-600 transition-colors inline-block mt-1" 
                                            v-if="rosterItem.player?.assigned_labels?.length > 0"
                                            @click="showLabelsPopup(rosterItem.player.assigned_labels, rosterItem.player, selectedTeam)"
                                            title="Click to view and manage all available labels"
                                          >
                                              {{ rosterItem.player.assigned_labels.map(label => label.player_label?.label || label.player_label?.name || label.player_label?.label_name).filter(l => !!l).join(', ') }}
                                          </div>
                                          <div class="text-xs text-gray-500 mt-1">
                                            <span v-if="rosterItem.player?.position">Position: {{ rosterItem.player.position }}</span>
                                            <span v-if="rosterItem.player?.position && rosterItem.player?.nationality"> • </span>
                                            <span v-if="rosterItem.player?.nationality">Nationality: {{ rosterItem.player.nationality }}</span>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      <!-- Column 2: Avg Time -->
                                      <button
                                        type="button"
                                        class="text-xs text-left text-gray-700 font-medium hover:text-blue-600 hover:underline cursor-pointer"
                                        :title="rosterItem.player?.teamSeasonMinutes ? `${rosterItem.player.teamSeasonMinutes.games} of ${rosterItem.player.teamSeasonMinutes.team_games} team games played this season. Click for the games.` : 'No RealGM data for this team'"
                                        @click.stop="openAvgTimeModal(rosterItem.player)"
                                      >
                                        {{ rosterItem.player?.teamSeasonMinutes?.avg != null ? rosterItem.player.teamSeasonMinutes.avg.toFixed(2) : '—' }}
                                      </button>
                                      
                                      <!-- Column 3: Last 3 Games (Minutes) -->
                                      <div class="flex items-center space-x-1">
                                        <template v-if="rosterItem.player?.lastGames !== undefined && rosterItem.player.lastGames.length > 0">
                                          <div 
                                            v-for="(game, index) in rosterItem.player.lastGames" 
                                            :key="index"
                                            class="w-[35px] flex justify-start"
                                            :title="game.is_unknown ? (game.data_note || 'Participation unknown') : (game.is_dnp ? 'DNP' : `Game ${index + 1}: ${game.minutes || 0} minutes`)"
                                          >
                                            <div 
                                              class="text-[10px] font-semibold px-2 py-1 rounded w-[35px] text-center"
                                              :class="game.is_dnp ? 'bg-gray-200 text-gray-600' : 'bg-blue-100 text-blue-700'"
                                            >
                                              {{ game.is_unknown ? 'DNP*' : (game.is_dnp ? 'DNP' : (game.minutes || game.minutes === 0 ? game.minutes : '—')) }}
                                            </div>
                                          </div>
                                        </template>
                                        <div 
                                          v-else-if="rosterItem.player?.lastGames !== undefined && rosterItem.player.lastGames.length === 0"
                                          class="text-[10px] text-gray-400 italic"
                                        >
                                          No games
                                        </div>
                                        <div 
                                          v-else
                                          class="text-[10px] text-gray-400 italic"
                                        >
                                          Loading...
                                        </div>
                                      </div>
                            
                                      
                                      <!-- Column 4: Actions -->
                                      <div class="flex items-center justify-end space-x-2 flex-shrink-0 text-right">
                                        <span 
                                          class="inline-flex items-center justify-center flex-shrink-0 px-2 py-1 w-6 cursor-pointer hover:bg-gray-100 rounded transition-colors"
                                          @click.stop="openPlayerNewsModal(rosterItem)"
                                          :title="hasUnreadNews(rosterItem) ? 'Unread news — click to view' : 'View news'"
                                        >
                                          <i 
                                            v-if="hasUnreadNews(rosterItem)"
                                            class="fas fa-envelope text-red-500 text-lg"
                                          ></i>
                                        </span>
                                        <span class="text-xs" :class="shouldShowNo(rosterItem) ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'">
                                          {{ shouldShowNo(rosterItem) ? 'no' : 'yes' }}
                                        </span>
                                        <!-- Player Status Dropdown -->
                                        <PlayerStatusDropdown
                                          v-if="rosterItem.player && rosterItem.player.player_team_status"
                                          v-model="rosterItem.player.player_team_status.player_status_id"
                                          :player-status="rosterItem.player.player_team_status.player_status_id"
                                          :player-id="rosterItem.id"
                                          @status-change="updatePlayerStatus"
                                        />
                                        
                                        <div class="text-xs text-gray-400">
                                          ID: {{ rosterItem.id }}
                                        </div>
                                        <button
                                          @click.stop="removePlayerFromRoster(rosterItem)"
                                          class="text-xs px-1 py-0.5 rounded flex items-center justify-center transition-all duration-150 group bg-transparent"
                                          title="Remove from roster"
                                        >
                                          <i class="fas fa-trash-alt text-red-400 opacity-30 grayscale group-hover:opacity-100 group-hover:grayscale-0 group-hover:text-red-600 transition-all duration-150 text-xs"></i>
                                        </button>
                                        <a 
                                          v-if="rosterItem.player?.player_link" 
                                          :href="rosterItem.player.player_link" 
                                          target="_blank" 
                                          class="text-blue-600 hover:text-blue-800 transition-colors"
                                          title="View on ProBallers"
                                        >
                                          <i class="fas fa-external-link-alt text-xs"></i>
                                        </a>
                                      </div>
                                    </div>
                                  </li>
                                </ul>
                                <div v-else class="text-gray-400 italic flex-1 text-sm">No players found for this team.</div>
                              </div>
                            </div>
                          </div>
                          
                          <!-- Context Menu -->
                          <div 
                            v-if="showContextMenu && contextMenuPlayer"
                            class="context-menu-player fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[200px]"
                            :style="{ left: contextMenuPosition.x + 'px', top: contextMenuPosition.y + 'px' }"
                            @click.stop
                          >
                            <div class="px-4 py-2 border-b border-gray-200">
                              <div class="font-medium text-sm text-gray-800">
                                {{ contextMenuPlayer.player?.player_name || 'Unknown Player' }}
                              </div>
                              <div class="text-xs text-gray-500 mt-1">
                                {{ contextMenuPlayer.player?.position || '' }}
                                <span v-if="contextMenuPlayer.player?.position && contextMenuPlayer.player?.nationality"> • </span>
                                {{ contextMenuPlayer.player?.nationality || '' }}
                              </div>
                            </div>
                            <button
                              @click.stop="handleContextMenuAction('view-profile')"
                              class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            >
                              <i class="fas fa-user text-gray-400"></i>
                              View Profile
                            </button>
                            <button
                              @click.stop="handleContextMenuAction('manage-labels')"
                              class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            >
                              <i class="fas fa-tags text-gray-400"></i>
                              Manage Labels
                            </button>
                            <button
                              @click.stop="handleContextMenuAction('position')"
                              class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            >
                              <i class="fas fa-exchange-alt text-gray-400"></i>
                              Update Position
                            </button>
                            <button
                              @click.stop="handleContextMenuAction('news')"
                              class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            >
                              <i class="fas fa-newspaper text-gray-400"></i>
                              News
                            </button>
                            <button
                              @click.stop="handleContextMenuAction('remove')"
                              class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                              <i class="fas fa-trash-alt text-red-400"></i>
                              Remove from Roster
                            </button>
                          </div>

                          <!-- Right Column - Team Leagues -->
                          <div class="hidden flex-1 flex flex-col min-h-0">
                            <div class="flex items-center justify-between mb-4">
                              <h5 class="text-md font-medium text-gray-700">Team Leagues ({{ selectedTeam.leagues?.length || 0 }})</h5>
                            </div>
                            
                            <!-- Two Column Layout: Search and Active Leagues -->
                            <div class="flex-1 flex gap-4 min-h-0">
                              <!-- Left Column - League Search (Small) -->
                              <div class="w-48 bg-white rounded-xs shadow border border-gray-100 p-4 flex flex-col min-h-0">
                                <div class="flex items-center mb-3">
                                  <i class="fas fa-search text-blue-500 mr-2"></i>
                                  <span class="font-semibold text-gray-700 text-sm">Search Leagues</span>
                                </div>
                                
                                <!-- Search Input -->
                                <div class="mb-4">
                                  <input
                                    v-model="leagueSearchQuery"
                                    @input="searchLeagues"
                                    type="text"
                                    placeholder="Search leagues..."
                                    class="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                                  />
                                </div>
                                
                                <!-- Search Results -->
                                <div class="flex-1 overflow-auto min-h-0">
                                  <div v-if="isSearchingLeagues" class="text-center text-gray-500 py-2">
                                    <i class="fas fa-spinner fa-spin mr-1 text-xs"></i>
                                    <span class="text-xs">Searching...</span>
                                  </div>
                                  
                                  <div v-else-if="leagueSearchResults.length > 0" class="space-y-1">
                                    <div class="text-xs font-medium text-gray-700 mb-1">
                                      Found {{ leagueSearchResults.length }} leagues
                                    </div>
                                    <div
                                      v-for="league in leagueSearchResults"
                                      :key="league.id"
                                      class="bg-gray-50 border border-gray-200 rounded p-1 hover:bg-gray-100 transition-colors"
                                    >
                                      <div class="flex items-center justify-between">
                                        <div class="flex-1 min-w-0">
                                          <div class="flex items-center space-x-1">
                                            <i class="fas fa-trophy text-gray-400 text-xs"></i>
                                            <span class="text-xs font-medium text-gray-800 truncate">{{ league.league_name || 'Unknown League' }}</span>
                                          </div>
                                          <div class="text-xs text-gray-500">
                                            <span v-if="league.region">{{ league.region }}</span>
                                            <span v-if="league.region && league.country"> • </span>
                                            <span v-if="league.country">{{ league.country }}</span>
                                          </div>
                                        </div>
                                        <button
                                          @click="addLeagueToTeam(league)"
                                          class="ml-1 px-1 py-0.5 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 transition-colors flex-shrink-0"
                                          title="Add to team"
                                        >
                                          <i class="fas fa-plus text-xs"></i>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div v-else-if="leagueSearchQuery && !isSearchingLeagues" class="text-center text-gray-500 py-2">
                                    <i class="fas fa-search text-lg mb-1"></i>
                                    <p class="text-xs">No leagues found</p>
                                  </div>
                                  
                                  <div v-else class="text-center text-gray-500 py-2">
                                    <i class="fas fa-search text-lg mb-1"></i>
                                    <p class="text-xs">Search for leagues</p>
                                  </div>
                                </div>
                              </div>

                              <!-- Right Column - Active Leagues -->
                              <div class="flex-1 bg-white rounded-xs shadow border border-gray-100 p-4 flex flex-col min-h-0">
                                <div class="flex items-center mb-2">
                                  <i class="fas fa-trophy text-blue-500 mr-2"></i>
                                  <span class="font-semibold text-gray-700 text-sm">Current Team Leagues</span>
                                </div>
                                <ul v-if="selectedTeam.leagues && selectedTeam.leagues.length > 0" class="space-y-1 flex-1 overflow-auto min-h-0">
                                  <li 
                                    v-for="leagueItem in selectedTeam.leagues" 
                                    :key="leagueItem.id" 
                                    class="bg-gray-50 rounded transition-colors"
                                  >
                                    <!-- League Item -->
                                    <div class="flex items-center justify-between px-2 py-1 hover:bg-gray-100 transition-colors cursor-pointer">
                                      <div class="flex items-center space-x-2">
                                        <i class="fas fa-trophy text-gray-400"></i>
                                        <div>
                                          <span class="font-medium text-sm">{{ leagueItem.league_name || 'Unknown League' }}</span>
                                          <div class="text-xs text-gray-500">
                                            <span v-if="leagueItem.region">{{ leagueItem.region }}</span>
                                            <span v-if="leagueItem.region && leagueItem.country"> • </span>
                                            <span v-if="leagueItem.country">{{ leagueItem.country }}</span>
                                            <span class="ml-2 text-gray-400">ID: {{ leagueItem.id }}</span>
                                          </div>
                                        </div>
                                      </div>
                                      <div class="flex items-center space-x-1">
                                        <!-- League Status Dropdown -->
                                        <select
                                          v-model="leagueItem.team_league_status"
                                          @change="updateLeagueStatus(leagueItem)"
                                          class="text-xs px-1 py-0.5 rounded border border-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                          :class="{
                                            'bg-green-100 text-green-800 border-green-300': leagueItem.team_league_status === 'active',
                                            'bg-red-100 text-red-800 border-red-300': leagueItem.team_league_status === 'inactive',
                                            'bg-orange-100 text-orange-800 border-orange-300': leagueItem.team_league_status === 'pending'
                                          }"
                                        >
                                          <option value="active" class="bg-green-50 text-green-800">Active</option>
                                          <option value="pending" class="bg-orange-50 text-orange-800">Pending</option>
                                          <option value="inactive" class="bg-red-50 text-red-800">Inactive</option>
                                        </select>
                                        
                                        <button
                                          @click.stop="removeLeagueFromTeam(leagueItem)"
                                          class="text-xs px-1 py-0.5 rounded flex items-center justify-center transition-all duration-150 group bg-transparent"
                                          title="Remove from team"
                                        >
                                          <i class="fas fa-trash-alt text-red-400 opacity-30 grayscale group-hover:opacity-100 group-hover:grayscale-0 group-hover:text-red-600 transition-all duration-150 text-xs"></i>
                                        </button>
                                      </div>
                                    </div>
                                  </li>
                                </ul>
                                <div v-else class="text-gray-400 italic flex-1 text-sm">No leagues found for this team.</div>
                              </div>
                            </div>
                          </div>

                          <!-- Right Column - Projections Stats Mapping -->
                          <div v-if="selectedTeam" class="hidden  w-80 bg-white rounded-xs shadow border border-gray-100 p-4 flex flex-col min-h-0">
                            <div class="flex items-center justify-between mb-4">
                              <div class="flex items-center">
                                <i class="fas fa-chart-line text-pink-500 mr-2"></i>
                                <span class="font-semibold text-gray-700">Stats Mapping</span>
                              </div>
                              <button
                                @click="clearTeamSelection"
                                class="text-gray-400 hover:text-gray-600 transition-colors"
                                title="Close mapping panel"
                              >
                                <i class="fas fa-times"></i>
                              </button>
                            </div>
                            
                            <!-- Selected Team Info -->
                            <div class="bg-pink-50 border border-pink-200 rounded-lg p-3 mb-4">
                              <div class="flex items-center space-x-2">
                                <i class="fas fa-users text-pink-500"></i>
                                <div>
                                  <span class="font-medium text-pink-800 text-sm">{{ selectedTeam?.team_name || selectedTeam?.team?.team_name || 'Unknown Team' }}</span>
                                  <div class="text-xs text-pink-600">
                                    <span v-if="selectedTeam?.region">{{ selectedTeam.region }}</span>
                                    <span v-if="selectedTeam?.region && selectedTeam?.league"> • </span>
                                    <span v-if="selectedTeam?.league">{{ selectedTeam.league }}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <!-- Search Section -->
                            <div cla  ss="mb-4">
                              <div class="flex items-center mb-2">
                                <i class="fas fa-search text-pink-500 mr-2"></i>
                                <span class="font-semibold text-gray-700 text-sm">Search Stats</span>
                              </div>
                              <input
                                v-model="projectionsSearchQuery"
                                @input="searchProjectionsStats"
                                type="text"
                                placeholder="Search stats..."
                                class="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                              />
                            </div>
                            
                            <!-- Search Results -->
                            <div class="flex-1 overflow-auto min-h-0">
                              <div v-if="isSearchingProjections" class="text-center text-gray-500 py-4">
                                <i class="fas fa-spinner fa-spin mr-2"></i>
                                <span class="text-sm">Searching...</span>
                              </div>
                              
                              <div v-else-if="projectionsSearchResults.length > 0" class="space-y-2">
                                <div class="text-xs font-medium text-gray-700 mb-2">
                                  Found {{ projectionsSearchResults.length }} results
                                </div>
                                
                                <!-- Two Column Layout for Team Totals vs Opponent Totals -->
                                <div class="flex gap-2">
                                  <!-- Team Totals Column -->
                                  <div class="flex-1">
                                    <div class="text-xs font-medium text-gray-600 mb-2 flex items-center">
                                      <i class="fas fa-users text-pink-500 mr-1"></i>
                                      Team Totals
                                    </div>
                                    <div class="space-y-2">
                                      <div
                                        v-for="projectionTeam in teamTotalsResults"
                                        :key="projectionTeam.id"
                                        class="bg-gray-50 border border-gray-200 rounded p-2 hover:bg-gray-100 transition-colors"
                                      >
                                        <div class="space-y-2">
                                          <div class="flex items-center justify-between">
                                            <div class="flex items-center space-x-2">
                                              <i class="fas fa-chart-bar text-pink-400 text-xs"></i>
                                              <span class="text-sm font-medium text-gray-800">{{ projectionTeam?.team_name || 'Unknown Team' }}</span>
                                            </div>
                                            <span class="text-xs px-1 py-0.5 bg-pink-100 text-pink-700 rounded">{{ formatDataType(projectionTeam?.data_type) }}</span>
                                          </div>
                                          <div class="text-xs text-gray-600 space-y-1">
                                            <div><span class="font-medium">League:</span> {{ projectionTeam?.league || 'Unknown League' }}</div>
                                          </div>
                                          <div class="flex space-x-2">
                                            <button
                                              @click="addProjectionTeamToTeam(projectionTeam, selectedTeam)"
                                              class="flex-1 px-2 py-1 bg-pink-600 text-white text-xs rounded hover:bg-pink-700 transition-colors"
                                              title="Add this projections team to the team"
                                            >
                                              <i class="fas fa-plus mr-1"></i>
                                              Add
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      <!-- Empty state for Team Totals -->
                                      <div v-if="teamTotalsResults.length === 0" class="text-center text-gray-400 py-4">
                                        <i class="fas fa-users text-xl mb-2"></i>
                                        <p class="text-xs">No team totals found</p>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <!-- Opponent Totals Column -->
                                  <div class="flex-1">
                                    <div class="text-xs font-medium text-gray-600 mb-2 flex items-center">
                                      <i class="fas fa-shield-alt text-pink-500 mr-1"></i>
                                      Opponent Totals
                                    </div>
                                    <div class="space-y-2">
                                      <div
                                        v-for="projectionTeam in opponentTotalsResults"
                                        :key="projectionTeam.id"
                                        class="bg-gray-50 border border-gray-200 rounded p-2 hover:bg-gray-100 transition-colors"
                                      >
                                        <div class="space-y-2">
                                          <div class="flex items-center justify-between">
                                            <div class="flex items-center space-x-2">
                                              <i class="fas fa-chart-bar text-pink-400 text-xs"></i>
                                              <span class="text-sm font-medium text-gray-800">{{ projectionTeam?.team_name || 'Unknown Team' }}</span>
                                            </div>
                                            <span class="text-xs px-1 py-0.5 bg-pink-100 text-pink-700 rounded">{{ formatDataType(projectionTeam?.data_type) }}</span>
                                          </div>
                                          <div class="text-xs text-gray-600 space-y-1">
                                            <div><span class="font-medium">League:</span> {{ projectionTeam?.league || 'Unknown League' }}</div>
                                          </div>
                                          <div class="flex space-x-2">
                                            <button
                                              @click="addProjectionTeamToTeam(projectionTeam, selectedTeam)"
                                              class="flex-1 px-2 py-1 bg-pink-600 text-white text-xs rounded hover:bg-pink-700 transition-colors"
                                              title="Add this projections team to the team"
                                            >
                                              <i class="fas fa-plus mr-1"></i>
                                              Add
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      <!-- Empty state for Opponent Totals -->
                                      <div v-if="opponentTotalsResults.length === 0" class="text-center text-gray-400 py-4">
                                        <i class="fas fa-shield-alt text-xl mb-2"></i>
                                        <p class="text-xs">No opponent totals found</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              <div v-else-if="projectionsSearchQuery && !isSearchingProjections" class="text-center text-gray-500 py-4">
                                <i class="fas fa-search text-xl mb-2"></i>
                                <p class="text-sm">No projections found</p>
                              </div>
                              
                              <div v-else class="text-center text-gray-500 py-4">
                                <i class="fas fa-chart-line text-xl mb-2"></i>
                                <p class="text-sm">Search for stats</p>
                                <p class="text-xs">Enter a team name to find matching stats data</p>
                              </div>
                            </div>
                          </div>

                          <!-- Empty Right Panel -->
                          <div v-else class="w-80 bg-gray-50 rounded-xs border border-gray-200 flex items-center justify-center">
                            <div class="text-center text-gray-500">
                              <i class="fas fa-mouse-pointer text-2xl mb-2"></i>
                              <p class="text-sm">Select a team to add stats</p>
                              <p class="text-xs">Click on any team from the middle panel</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Depth Chart Table -->
                      <div class="flex-1 flex flex-col min-h-0 mt-4">
                        <div class="flex items-center justify-between mb-4">
                          <h4 class="text-lg font-medium text-gray-800">Depth Chart</h4>
                          <button
                            @click="autoFillDepthChart"
                            class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
                            title="Auto-fill depth chart based on average minutes"
                          >
                            <i class="fas fa-magic mr-1"></i>
                            Auto Fill
                          </button>
                        </div>
                        
                        <!-- Depth Chart Container -->
                        <div class="bg-white rounded-xs shadow border border-gray-100 p-4 flex flex-col min-h-0">
                          <div class="flex items-center justify-between mb-4">
                            <div class="flex items-center">
                              <i class="fas fa-table text-blue-500 mr-2"></i>
                              <span class="font-semibold text-gray-700">Team Depth Chart</span>
                            </div>
                            <div v-if="isLoadingDepthChart" class="flex items-center text-sm text-gray-500">
                              <i class="fas fa-spinner fa-spin mr-2"></i>
                              Loading depth chart...
                            </div>
                          </div>
                          
                          <!-- Depth Chart Table -->
                          <div v-if="!selectedTeam" class="flex items-center justify-center h-32 text-gray-500">
                            <div class="text-center">
                              <i class="fas fa-users text-2xl mb-2"></i>
                              <p>Select a team to view depth chart</p>
                            </div>
                          </div>
                          <div v-else-if="isLoadingDepthChart" class="flex items-center justify-center h-32 text-gray-500">
                            <div class="text-center">
                              <i class="fas fa-spinner fa-spin text-2xl mb-2"></i>
                              <p>Loading depth chart...</p>
                            </div>
                          </div>
                          <div v-else-if="!depthChartData" class="flex items-center justify-center h-32 text-gray-500">
                            <div class="text-center">
                              <i class="fas fa-exclamation-triangle text-2xl mb-2"></i>
                              <p>No depth chart data available</p>
                            </div>
                          </div>
                          <div v-else class="overflow-x-auto">
                            <table class="w-full border-collapse">
                              <thead>
                                <tr class="bg-gray-50">
                                  <th class="border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-700">Role</th>
                                  <th class="border border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-700">PG</th>
                                  <th class="border border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-700">SG</th>
                                  <th class="border border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-700">SF</th>
                                  <th class="border border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-700">PF</th>
                                  <th class="border border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-700">C</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr class="hover:bg-gray-50">
                                  <td class="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 bg-green-50">Starter</td>
                                  <td 
                                    @click="openPlayerSelection('PG', 'starter')"
                                    @contextmenu.prevent="clearPlayerFromCell('PG', 'starter')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('PG', 'starter')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('PG', 'starter')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('PG', 'starter'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('PG', 'starter')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('PG', 'starter') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('PG', 'starter')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                      
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('SG', 'starter')"
                                    @contextmenu.prevent="clearPlayerFromCell('SG', 'starter')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('SG', 'starter')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('SG', 'starter')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('SG', 'starter'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('SG', 'starter')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('SG', 'starter') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('SG', 'starter')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('SF', 'starter')"
                                    @contextmenu.prevent="clearPlayerFromCell('SF', 'starter')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('SF', 'starter')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('SF', 'starter')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('SF', 'starter'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('SF', 'starter')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('SF', 'starter') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('SF', 'starter')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('PF', 'starter')"
                                    @contextmenu.prevent="clearPlayerFromCell('PF', 'starter')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('PF', 'starter')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('PF', 'starter')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('PF', 'starter'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('PF', 'starter')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('PF', 'starter') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('PF', 'starter')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('C', 'starter')"
                                    @contextmenu.prevent="clearPlayerFromCell('C', 'starter')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('C', 'starter')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('C', 'starter')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('C', 'starter'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('C', 'starter')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('C', 'starter') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('C', 'starter')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                </tr>

                                <tr class="hover:bg-gray-50">
                                  <td class="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 bg-blue-50">Rotational Player</td>
                                  <td 
                                    @click="openPlayerSelection('PG', 'rotational')"
                                    @contextmenu.prevent="clearPlayerFromCell('PG', 'rotational')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('PG', 'rotational')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('PG', 'rotational')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('PG', 'rotational'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('PG', 'rotational')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('PG', 'rotational') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('PG', 'rotational')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('SG', 'rotational')"
                                    @contextmenu.prevent="clearPlayerFromCell('SG', 'rotational')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('SG', 'rotational')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('SG', 'rotational')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('SG', 'rotational'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('SG', 'rotational')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('SG', 'rotational') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('SG', 'rotational')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('SF', 'rotational')"
                                    @contextmenu.prevent="clearPlayerFromCell('SF', 'rotational')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('SF', 'rotational')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('SF', 'rotational')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('SF', 'rotational'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('SF', 'rotational')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('SF', 'rotational') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('SF', 'rotational')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('PF', 'rotational')"
                                    @contextmenu.prevent="clearPlayerFromCell('PF', 'rotational')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('PF', 'rotational')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('PF', 'rotational')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('PF', 'rotational'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('PF', 'rotational')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('PF', 'rotational') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('PF', 'rotational')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('C', 'rotational')"
                                    @contextmenu.prevent="clearPlayerFromCell('C', 'rotational')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('C', 'rotational')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('C', 'rotational')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('C', 'rotational'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('C', 'rotational')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('C', 'rotational') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('C', 'rotational')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                </tr>

                                <tr class="hover:bg-gray-50">
                                  <td class="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 bg-blue-50">In & Out of Rotation</td>
                                  <td 
                                    @click="openPlayerSelection('PG', 'in_out_rotation')"
                                    @contextmenu.prevent="clearPlayerFromCell('PG', 'in_out_rotation')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('PG', 'in_out_rotation')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('PG', 'in_out_rotation')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('PG', 'in_out_rotation'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('PG', 'in_out_rotation')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('PG', 'in_out_rotation') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('PG', 'in_out_rotation')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('SG', 'in_out_rotation')"
                                    @contextmenu.prevent="clearPlayerFromCell('SG', 'in_out_rotation')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('SG', 'in_out_rotation')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('SG', 'in_out_rotation')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('SG', 'in_out_rotation'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('SG', 'in_out_rotation')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('SG', 'in_out_rotation') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('SG', 'in_out_rotation')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('SF', 'in_out_rotation')"
                                    @contextmenu.prevent="clearPlayerFromCell('SF', 'in_out_rotation')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('SF', 'in_out_rotation')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('SF', 'in_out_rotation')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('SF', 'in_out_rotation'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('SF', 'in_out_rotation')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('SF', 'in_out_rotation') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('SF', 'in_out_rotation')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('PF', 'in_out_rotation')"
                                    @contextmenu.prevent="clearPlayerFromCell('PF', 'in_out_rotation')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('PF', 'in_out_rotation')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('PF', 'in_out_rotation')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('PF', 'in_out_rotation'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('PF', 'in_out_rotation')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('PF', 'in_out_rotation') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('PF', 'in_out_rotation')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('C', 'in_out_rotation')"
                                    @contextmenu.prevent="clearPlayerFromCell('C', 'in_out_rotation')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('C', 'in_out_rotation')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('C', 'in_out_rotation')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('C', 'in_out_rotation'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('C', 'in_out_rotation')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('C', 'in_out_rotation') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('C', 'in_out_rotation')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                </tr>
                                <tr class="hover:bg-gray-50">
                                  <td class="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 bg-yellow-50">Injury Backup</td>
                                  <td 
                                    @click="openPlayerSelection('PG', 'injury_backup')"
                                    @contextmenu.prevent="clearPlayerFromCell('PG', 'injury_backup')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('PG', 'injury_backup')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('PG', 'injury_backup')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('PG', 'injury_backup'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('PG', 'injury_backup')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('PG', 'injury_backup') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('PG', 'injury_backup')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('SG', 'injury_backup')"
                                    @contextmenu.prevent="clearPlayerFromCell('SG', 'injury_backup')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('SG', 'injury_backup')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('SG', 'injury_backup')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('SG', 'injury_backup'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('SG', 'injury_backup')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('SG', 'injury_backup') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('SG', 'injury_backup')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('SF', 'injury_backup')"
                                    @contextmenu.prevent="clearPlayerFromCell('SF', 'injury_backup')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('SF', 'injury_backup')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('SF', 'injury_backup')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('SF', 'injury_backup'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('SF', 'injury_backup')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('SF', 'injury_backup') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('SF', 'injury_backup')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('PF', 'injury_backup')"
                                    @contextmenu.prevent="clearPlayerFromCell('PF', 'injury_backup')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('PF', 'injury_backup')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('PF', 'injury_backup')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('PF', 'injury_backup'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('PF', 'injury_backup')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('PF', 'injury_backup') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('PF', 'injury_backup')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('C', 'injury_backup')"
                                    @contextmenu.prevent="clearPlayerFromCell('C', 'injury_backup')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('C', 'injury_backup')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('C', 'injury_backup')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('C', 'injury_backup'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('C', 'injury_backup')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('C', 'injury_backup') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('C', 'injury_backup')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                </tr>
                                <tr class="hover:bg-gray-50">
                                  <td class="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 bg-yellow-50">Injury Backup 2</td>
                                  <td 
                                    @click="openPlayerSelection('PG', 'injury_backup_2')"
                                    @contextmenu.prevent="clearPlayerFromCell('PG', 'injury_backup_2')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('PG', 'injury_backup_2')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('PG', 'injury_backup_2')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('PG', 'injury_backup_2'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('PG', 'injury_backup_2')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('PG', 'injury_backup_2') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('PG', 'injury_backup_2')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('SG', 'injury_backup_2')"
                                    @contextmenu.prevent="clearPlayerFromCell('SG', 'injury_backup_2')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('SG', 'injury_backup_2')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('SG', 'injury_backup_2')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('SG', 'injury_backup_2'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('SG', 'injury_backup_2')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('SG', 'injury_backup_2') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('SG', 'injury_backup_2')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('SF', 'injury_backup_2')"
                                    @contextmenu.prevent="clearPlayerFromCell('SF', 'injury_backup_2')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('SF', 'injury_backup_2')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('SF', 'injury_backup_2')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('SF', 'injury_backup_2'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('SF', 'injury_backup_2')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('SF', 'injury_backup_2') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('SF', 'injury_backup_2')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('PF', 'injury_backup_2')"
                                    @contextmenu.prevent="clearPlayerFromCell('PF', 'injury_backup_2')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('PF', 'injury_backup_2')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('PF', 'injury_backup_2')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('PF', 'injury_backup_2'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('PF', 'injury_backup_2')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('PF', 'injury_backup_2') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('PF', 'injury_backup_2')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('C', 'injury_backup_2')"
                                    @contextmenu.prevent="clearPlayerFromCell('C', 'injury_backup_2')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('C', 'injury_backup_2')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('C', 'injury_backup_2')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('C', 'injury_backup_2'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('C', 'injury_backup_2')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('C', 'injury_backup_2') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('C', 'injury_backup_2')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                </tr>
                                <tr class="hover:bg-gray-50">
                                  <td class="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 bg-yellow-50">Injury Backup 3</td>
                                  <td 
                                    @click="openPlayerSelection('PG', 'injury_backup_3')"
                                    @contextmenu.prevent="clearPlayerFromCell('PG', 'injury_backup_3')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('PG', 'injury_backup_3')" class="text-xs relative">
                                      <span class="block text-gray-400 text-[10px] leading-none mb-0.5" v-if="getDepthChartPlayerStatus('PG', 'injury_backup_3')">
                                        {{ getDepthChartPlayerStatus('PG', 'injury_backup_3') }}
                                      </span>
                                          <span>{{ getDepthChartPlayer('PG', 'injury_backup_3') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('PG', 'injury_backup_3')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('SG', 'injury_backup_3')"
                                    @contextmenu.prevent="clearPlayerFromCell('SG', 'injury_backup_3')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('SG', 'injury_backup_3')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('SG', 'injury_backup_3')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('SG', 'injury_backup_3'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('SG', 'injury_backup_3')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('SG', 'injury_backup_3') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('SG', 'injury_backup_3')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('SF', 'injury_backup_3')"
                                    @contextmenu.prevent="clearPlayerFromCell('SF', 'injury_backup_3')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('SF', 'injury_backup_3')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('SF', 'injury_backup_3')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('SF', 'injury_backup_3'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('SF', 'injury_backup_3')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('SF', 'injury_backup_3') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('SF', 'injury_backup_3')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('PF', 'injury_backup_3')"
                                    @contextmenu.prevent="clearPlayerFromCell('PF', 'injury_backup_3')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('PF', 'injury_backup_3')" class="text-xs relative">
                                      <span class="block text-gray-400 text-[10px] leading-none mb-0.5" v-if="getDepthChartPlayerStatus('PF', 'injury_backup_3')">
                                        {{ getDepthChartPlayerStatus('PF', 'injury_backup_3') }}
                                      </span>
                                          <span>{{ getDepthChartPlayer('PF', 'injury_backup_3') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('PF', 'injury_backup_3')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('C', 'injury_backup_3')"
                                    @contextmenu.prevent="clearPlayerFromCell('C', 'injury_backup_3')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('C', 'injury_backup_3')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('C', 'injury_backup_3')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('C', 'injury_backup_3'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('C', 'injury_backup_3')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('C', 'injury_backup_3') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('C', 'injury_backup_3')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                </tr>
                          
                                <tr class="hover:bg-gray-50">
                                  <td class="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 bg-red-50">Out of Rotation</td>
                                  <td 
                                    @click="openPlayerSelection('PG', 'out_rotation')"
                                    @contextmenu.prevent="clearPlayerFromCell('PG', 'out_rotation')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('PG', 'out_rotation')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('PG', 'out_rotation')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('PG', 'out_rotation'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('PG', 'out_rotation')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('PG', 'out_rotation') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('PG', 'out_rotation')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('SG', 'out_rotation')"
                                    @contextmenu.prevent="clearPlayerFromCell('SG', 'out_rotation')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('SG', 'out_rotation')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('SG', 'out_rotation')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('SG', 'out_rotation'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('SG', 'out_rotation')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('SG', 'out_rotation') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('SG', 'out_rotation')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('SF', 'out_rotation')"
                                    @contextmenu.prevent="clearPlayerFromCell('SF', 'out_rotation')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('SF', 'out_rotation')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('SF', 'out_rotation')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('SF', 'out_rotation'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('SF', 'out_rotation')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('SF', 'out_rotation') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('SF', 'out_rotation')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('PF', 'out_rotation')"
                                    @contextmenu.prevent="clearPlayerFromCell('PF', 'out_rotation')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('PF', 'out_rotation')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('PF', 'out_rotation')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('PF', 'out_rotation'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('PF', 'out_rotation')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('PF', 'out_rotation') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('PF', 'out_rotation')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                  <td 
                                    @click="openPlayerSelection('C', 'out_rotation')"
                                    @contextmenu.prevent="clearPlayerFromCell('C', 'out_rotation')"
                                    class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
                                  >
                                    <div v-if="getDepthChartPlayer('C', 'out_rotation')" class="text-xs relative flex items-center justify-center gap-1">
                                      <div class="inline-block w-4 h-4 flex items-center justify-center rounded-full text-[8px]"
                                           v-if="getDepthChartPlayerStatus('C', 'out_rotation')"
                                           :class="getStatusBadgeClass(getDepthChartPlayerStatus('C', 'out_rotation'))">
                                        {{ getStatusLetter(getDepthChartPlayerStatus('C', 'out_rotation')) }}
                                      </div>
                                      <span>{{ getDepthChartPlayer('C', 'out_rotation') }}</span>
                                      <button 
                                        @click.stop="clearPlayerFromCell('C', 'out_rotation')"
                                        class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                        title="Remove player"
                                      >
                                        <i class="fas fa-times"></i>
                                      </button>
                                    </div>
                                    <div v-else class="text-gray-400 text-xs">Click to select</div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      <!-- Action Buttons -->
                      <div class="flex justify-end space-x-3 flex-shrink-0 mt-4">
                        <button
                          @click="saveTeamSettings"
                          class="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <i class="fas fa-save mr-2"></i>
                          Save Team
                        </button>
                        <button
                          @click="deleteTeam"
                          class="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          <i class="fas fa-trash mr-2"></i>
                          Delete Team
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Empty State -->
                <div v-if="!selectedTeam" class="flex items-center justify-center h-full">
                  <div class="text-center text-gray-500">
                    <i class="fas fa-users text-4xl mb-4"></i>
                    <p class="text-lg">Search for teams to view their details</p>
                    <p class="text-sm">Click on any team from the search results to view its information</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

     
        <!-- Player Labels Tab -->
        <div v-if="activeTab === 'player_labels'" class="w-full h-full flex">
          <!-- Left Sidebar Menu -->
          <div class="w-[240px] h-full bg-gradient-to-b from-gray-50 to-gray-100 border-r border-gray-200 flex flex-col shadow-sm">
            <div class="p-4 border-b border-gray-200">
              <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Player Labels</h3>
            </div>
            <div class="flex-1 py-2">
              <button
                class="w-full px-4 py-3 text-left hover:bg-blue-50 focus:bg-blue-100 transition-all duration-200 font-medium text-gray-700 border-l-4 border-transparent hover:border-blue-400 focus:border-blue-500"
                :class="{'bg-blue-50 border-blue-500 text-blue-700': playerLabelMenu === 'projection_weights'}"
                @click="playerLabelMenu = 'projection_weights'; fetchDataForCurrentMenu()"
              >
                <div class="flex items-center">
                  <i class="fas fa-chart-line text-blue-500 mr-3 w-4"></i>
                  <div>
                    <div class="font-medium">Projection Weights</div>
                    <div class="text-xs text-gray-500 mt-1">Configure projection weights</div>
                  </div>
                </div>
              </button>
              <button
                class="w-full px-4 py-3 text-left hover:bg-green-50 focus:bg-green-100 transition-all duration-200 font-medium text-gray-700 border-l-4 border-transparent hover:border-green-400 focus:border-green-500"
                :class="{'bg-green-50 border-green-500 text-green-700': playerLabelMenu === 'minutes_settings_weights'}"
                @click="playerLabelMenu = 'minutes_settings_weights'; fetchDataForCurrentMenu()"
              >
                <div class="flex items-center">
                  <i class="fas fa-clock text-green-500 mr-3 w-4"></i>
                  <div>
                    <div class="font-medium">Minutes Settings</div>
                    <div class="text-xs text-gray-500 mt-1">Configure minutes weights</div>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <!-- Main Content Area -->
          <div class="flex-1 h-full overflow-y-auto bg-gray-50">
            <!-- Projection Weights Content -->
            <div v-if="playerLabelMenu === 'projection_weights'" class="min-h-full p-8">
              <div class="max-w-7xl mx-auto">
                <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div class="bg-gradient-to-r from-blue-50 to-blue-100 px-8 py-6 border-b border-gray-200">
                    <h3 class="text-xl font-semibold text-gray-800 flex items-center">
                      <i class="fas fa-chart-line text-blue-600 mr-3"></i>
                      Player Label Projection Weights
                    </h3>
                    <p class="text-sm text-gray-600 mt-2">Configure projection weights for different player categories and game ranges</p>
                  </div>
                  
                  <div class="overflow-x-auto">
                    <table class="w-full">
                      <thead class="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th class="px-6 py-2 text-left text-sm font-semibold text-gray-700">Label</th>
                          <th class="px-6 py-2 text-left text-sm font-semibold text-gray-700">Games</th>
                          <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">Last Season</th>
                          <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">2 Seasons ago</th>
                          <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">3 Seasons ago</th>
                          <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">League Avg</th>
                          <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">Last 5 games</th>
                          <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">Rest of Curr Season</th>
                          <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-for="(label, labelIndex) in labelProjectionData" :key="labelIndex">
                          <tr 
                            v-for="(row, rowIndex) in label.rows" 
                            :key="`${labelIndex}-${rowIndex}`" 
                            class="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                          >
                            <td 
                              v-if="rowIndex === 0" 
                              :rowspan="label.rows.length" 
                              class="px-6 py-2 text-sm text-gray-800 font-medium border-r border-gray-200 align-top"
                              style="vertical-align: top;"
                            >
                              <div class="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-lg text-sm font-medium shadow-sm mt-0">
                                <i class="fas fa-tag text-blue-600 mr-2"></i>
                                {{ label.name }}
                              </div>
                            </td>
                            <td class="px-6 py-2 text-sm text-gray-600 border-r border-gray-200 font-medium">{{ row.games }}</td>
                            <td class="px-6 py-2 text-center">
                              <input type="number" step="0.01" class="w-20 text-center border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" v-model="row.lastSeason" />
                            </td>
                            <td class="px-6 py-2 text-center">
                              <input type="number" step="0.01" class="w-20 text-center border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" v-model="row.twoSeasonsAgo" />
                            </td>
                            <td class="px-6 py-2 text-center">
                              <input type="number" step="0.01" class="w-20 text-center border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" v-model="row.threeSeasonsAgo" />
                            </td>
                            <td class="px-6 py-2 text-center">
                              <input type="number" step="0.01" class="w-20 text-center border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" v-model="row.leagueAvg" />
                            </td>
                            <td class="px-6 py-2 text-center">
                              <input type="number" step="0.01" class="w-20 text-center border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" v-model="row.lastFiveGames" />
                            </td>
                            <td class="px-6 py-2 text-center">
                              <input type="number" step="0.01" class="w-20 text-center border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" v-model="row.restOfSeason" />
                            </td>
                            <td 
                              class="px-6 py-2 text-center text-gray-800 font-semibold"
                              :class="{
                                'bg-red-100 text-red-700': (
                                  (parseFloat(row.lastSeason) || 0) +
                                  (parseFloat(row.twoSeasonsAgo) || 0) +
                                  (parseFloat(row.threeSeasonsAgo) || 0) +
                                  (parseFloat(row.leagueAvg) || 0) +
                                  (parseFloat(row.lastFiveGames) || 0) +
                                  (parseFloat(row.restOfSeason) || 0)
                                ).toFixed(2) != '1.00'
                              }"
                            >
                              {{
                                (
                                  (parseFloat(row.lastSeason) || 0) +
                                  (parseFloat(row.twoSeasonsAgo) || 0) +
                                  (parseFloat(row.threeSeasonsAgo) || 0) +
                                  (parseFloat(row.leagueAvg) || 0) +
                                  (parseFloat(row.lastFiveGames) || 0) +
                                  (parseFloat(row.restOfSeason) || 0)
                                ).toFixed(2)
                              }}
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>
                  
                  <div class="bg-gray-50 px-8 py-6 border-t border-gray-200 flex justify-end">
                    <button 
                      @click="saveWeights"
                      :disabled="isSaving"
                      class="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i v-if="isSaving" class="fas fa-spinner fa-spin mr-2"></i>
                      <i v-else class="fas fa-save mr-2"></i>
                      {{ isSaving ? 'Saving...' : 'Save Projection Weights' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Minutes Settings Weights Content -->
            <div v-if="playerLabelMenu === 'minutes_settings_weights'" class="min-h-full p-8 bg-gray-100">
              <div class="max-w-7xl mx-auto">
                <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div class="bg-gradient-to-r from-green-50 to-green-100 px-8 py-6 border-b border-gray-200">
                    <h3 class="text-xl font-semibold text-gray-800 flex items-center">
                      <i class="fas fa-clock text-green-600 mr-3"></i>
                      Minutes Settings Weights
                    </h3>
                    <p class="text-sm text-gray-600 mt-2">Configure minutes settings weights for different player categories and minute ranges</p>
                  </div>
                  
                  <div class="overflow-x-auto">
                    <table class="w-full">
                      <thead class="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th class="px-6 py-2 text-left text-sm font-semibold text-gray-700">MINUTES</th>
                          <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">Games</th>
                          <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">Last Season</th>
                          <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">2 Seasons ago</th>
                          <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">3 Seasons ago</th>
                          <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">League Avg</th>
                          <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">Last 5 games</th>
                          <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">Rest of Curr Season</th>
                        <th class="px-6 py-2 text-center text-sm font-semibold text-gray-700">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-for="(label, labelIndex) in labelProjectionData" :key="labelIndex">
                          <tr v-for="(row, rowIndex) in label.rows" :key="`${labelIndex}-${rowIndex}`" class="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                            <td 
                              v-if="rowIndex === 0" 
                              :rowspan="label.rows.length" 
                              class="px-6 py-2 text-sm text-gray-800 font-medium border-r border-gray-200 align-top"
                              style="vertical-align: top;"
                            >
                              <div class="inline-flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-lg text-sm font-medium shadow-sm mt-0">
                                <i class="fas fa-clock text-green-600 mr-2"></i>
                                {{ label.name }}
                              </div>
                            </td>
                            <td class="px-6 py-2 text-sm text-gray-600 border-r border-gray-200 font-medium">{{ row.games }}</td>
                            <td class="px-6 py-2 text-center">
                              <input type="number" step="0.01" class="w-20 text-center border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors" v-model="row.lastSeason" />
                            </td>
                            <td class="px-6 py-2 text-center">
                              <input type="number" step="0.01" class="w-20 text-center border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors" v-model="row.twoSeasonsAgo" />
                            </td>
                            <td class="px-6 py-2 text-center">
                              <input type="number" step="0.01" class="w-20 text-center border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors" v-model="row.threeSeasonsAgo" />
                            </td>
                            <td class="px-6 py-2 text-center">
                              <input type="number" step="0.01" class="w-20 text-center border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors" v-model="row.leagueAvg" />
                            </td>
                            <td class="px-6 py-2 text-center">
                              <input type="number" step="0.01" class="w-20 text-center border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors" v-model="row.lastFiveGames" />
                            </td>
                            <td class="px-6 py-2 text-center">
                              <input type="number" step="0.01" class="w-20 text-center border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors" v-model="row.restOfSeason" />
                            </td>
                            <td class="px-6 py-2 text-center text-gray-800 font-semibold">
                              {{
                                (
                                  (parseFloat(row.lastSeason) || 0) +
                                  (parseFloat(row.twoSeasonsAgo) || 0) +
                                  (parseFloat(row.threeSeasonsAgo) || 0) +
                                  (parseFloat(row.leagueAvg) || 0) +
                                  (parseFloat(row.lastFiveGames) || 0) +
                                  (parseFloat(row.restOfSeason) || 0)
                                ).toFixed(2)
                              }}
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>
                  
                  <div class="bg-gray-50 px-8 py-6 border-t border-gray-200 flex justify-end">
                    <button 
                      @click="saveWeights"
                      :disabled="isSaving"
                      class="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i v-if="isSaving" class="fas fa-spinner fa-spin mr-2"></i>
                      <i v-else class="fas fa-save mr-2"></i>
                      {{ isSaving ? 'Saving...' : 'Save Minutes Settings Weights' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Season Details Modal -->
    <SeasonDetailsModal
      :season="seasonDetailsData.season"
      :player-id="seasonDetailsData.playerId"
      :show="showSeasonDetailsModal"
      :context="'splits'"
      @close="closeSeasonDetailsModal"
    />

    <!-- Player News Modal -->
    <PlayerNewsModal
      v-if="newsModalPlayer"
      :player-id="newsModalPlayer.id"
      :player-name="newsModalPlayer.name"
      :player-status="newsModalPlayer.status"
      :roster-item-id="newsModalPlayer.rosterItemId"
      :show="showPlayerNewsModal"
      @close="closePlayerNewsModal"
      @status-updated="handleNewsModalStatusUpdate"
    />

    <!-- Avg Time breakdown: which games (and leagues) the team-season average is made of -->
    <div
      v-if="avgTimeModalPlayer"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      @click.self="closeAvgTimeModal"
      @keyup.esc="closeAvgTimeModal"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[85vh] flex flex-col">
        <div class="flex items-start justify-between p-4 border-b border-gray-200">
          <div>
            <h3 class="text-base font-semibold text-gray-900">Avg Time · {{ avgTimeModalPlayer.player_name }}</h3>
            <p class="text-xs text-gray-500 mt-0.5">
              {{ selectedTeam?.team_name }}, this season only (from 1 Aug), RealGM.
              Average = minutes per played game; DNP and "not in box score" do not count.
            </p>
          </div>
          <button @click="closeAvgTimeModal" class="text-gray-400 hover:text-gray-600 transition-colors ml-3">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div v-if="!avgTimeModalPlayer.teamSeasonMinutes" class="p-4 text-sm text-gray-600">
          RealGM games for this team could not be loaded.
        </div>
        <div v-else class="p-4 overflow-y-auto space-y-4">
          <div class="text-sm text-gray-800">
            <span class="font-semibold">{{ avgTimeModalPlayer.teamSeasonMinutes.avg != null ? avgTimeModalPlayer.teamSeasonMinutes.avg.toFixed(2) : '—' }}</span>
            min from {{ avgTimeModalPlayer.teamSeasonMinutes.games }} played of {{ avgTimeModalPlayer.teamSeasonMinutes.team_games }} team games
          </div>

          <div v-if="avgTimeModalPlayer.teamSeasonMinutes.team_games === 0" class="text-sm text-gray-600">
            No games found for this team this season. If the team has played, check its RealGM team mapping.
          </div>

          <template v-else>
            <table class="w-full text-xs">
              <thead>
                <tr class="text-left text-gray-500 border-b border-gray-200">
                  <th class="py-1 pr-2 font-medium">League</th>
                  <th class="py-1 pr-2 font-medium text-right">Played</th>
                  <th class="py-1 font-medium text-right">Avg min</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="group in avgTimeLeagueGroups(avgTimeModalPlayer.teamSeasonMinutes)" :key="group.name" class="border-b border-gray-100">
                  <td class="py-1 pr-2 text-gray-800">{{ group.name }}</td>
                  <td class="py-1 pr-2 text-right text-gray-700">{{ group.played }} / {{ group.total }}</td>
                  <td class="py-1 text-right text-gray-700">{{ group.avg }}</td>
                </tr>
              </tbody>
            </table>

            <table class="w-full text-xs">
              <thead>
                <tr class="text-left text-gray-500 border-b border-gray-200">
                  <th class="py-1 pr-2 font-medium">Date</th>
                  <th class="py-1 pr-2 font-medium">Opponent</th>
                  <th class="py-1 pr-2 font-medium">League</th>
                  <th class="py-1 font-medium text-right">Min</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="game in avgTimeModalPlayer.teamSeasonMinutes.list"
                  :key="game.date"
                  class="border-b border-gray-100"
                  :class="game.status === 'played' ? 'text-gray-800' : 'text-gray-400'"
                >
                  <td class="py-1 pr-2 whitespace-nowrap">{{ game.date }}</td>
                  <td class="py-1 pr-2">{{ game.home ? 'vs' : '@' }} {{ game.opponent }}</td>
                  <td class="py-1 pr-2">
                    {{ game.league || '—' }}
                    <span v-if="game.source !== 'RealGM'" class="ml-1 px-1 rounded bg-amber-100 text-amber-800">{{ game.source }}</span>
                  </td>
                  <td class="py-1 text-right whitespace-nowrap">
                    <template v-if="game.status === 'played'">{{ game.minutes.toFixed(2) }}</template>
                    <template v-else-if="game.status === 'dnp'">DNP</template>
                    <template v-else>not in box score</template>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
      </div>
    </div>

    <!-- Position Update Modal -->
    <div
      v-if="showPositionModal && positionModalPlayer"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="closePositionModal"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-900">
            Update Position - {{ positionModalPlayer.name }}
          </h3>
          <button 
            @click="closePositionModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Position
          </label>
          <input
            v-model="editingPosition"
            @keyup.enter="savePlayerPosition"
            @keyup.esc="closePositionModal"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter position (e.g., PG, SG, SF, PF, C)"
            autofocus
          />
          <p class="text-xs text-gray-500 mt-1">
            Press Enter to save, Esc to cancel
          </p>
        </div>
        
        <div class="flex justify-end gap-2">
          <button
            @click="closePositionModal"
            class="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="savePlayerPosition"
            class="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>

    <!-- Player Selection Modal for Depth Chart -->
    <div
      v-if="showPlayerSelectionModal"
      class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      @click="closePlayerSelectionModal"
    >
      <div
        class="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        @click.stop
      >
        <!-- Modal Header -->
        <div class="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 class="text-lg font-semibold text-gray-800">
              Select Player for {{ selectedPosition }} - {{ selectedRole }}
            </h3>
            <p class="text-sm text-gray-600 mt-1">
              Choose a player from the current team roster
            </p>
          </div>
          <button
            @click="closePlayerSelectionModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i class="fas fa-times text-lg"></i>
          </button>
        </div>

        <!-- Modal Content -->
        <div class="flex-1 overflow-y-auto p-6">
          <div v-if="selectedTeam && selectedTeam.roster && selectedTeam.roster.length > 0" class="space-y-2">
            <div
              v-for="rosterItem in selectedTeam.roster"
              :key="rosterItem.id"
              @click="selectPlayerForDepthChart(rosterItem.player)"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors border border-gray-200"
            >
              <div class="flex items-center space-x-3">
                <i class="fas fa-user text-blue-500"></i>
                <div>
                  <div class="font-medium text-gray-800">
                    {{ rosterItem.player?.player_name || 'Unknown Player' }}
                  </div>
                  <div class="text-sm text-gray-600">
                    <span v-if="rosterItem.player?.position">{{ rosterItem.player.position }}</span>
                    <span v-if="rosterItem.player?.position && rosterItem.player?.nationality"> • </span>
                    <span v-if="rosterItem.player?.nationality">{{ rosterItem.player.nationality }}</span>
                  </div>
                </div>
              </div>
              <div class="text-sm text-gray-500">
                ID: {{ rosterItem.player?.p_player_id || rosterItem.id }}
              </div>
            </div>
          </div>
          <div v-else class="text-center text-gray-500 py-8">
            <i class="fas fa-users text-3xl mb-4"></i>
            <p>No players available in the team roster</p>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            @click="closePlayerSelectionModal"
            class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Labels Popup Modal -->
    <div
      v-if="showLabelsModal"
      class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      @click="closeLabelsPopup"
    >
      <div
        class="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
        @click.stop
      >
        <!-- Modal Header -->
        <div class="p-6 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-xl font-semibold text-gray-800">Player Labels</h3>
              <p class="text-gray-600 mt-1">
                <span v-if="currentPlayer" class="font-medium">{{ currentPlayer.player_name || 'Unknown Player' }}</span>
                <span v-if="currentPlayer && currentTeam" class="text-sm text-gray-500 ml-2">
                  • {{ currentTeam.team_name || currentTeam.name || 'Current Team' }}
                </span>
                <span v-else>All available labels in the system</span>
              </p>
            </div>
            <button
              @click="closeLabelsPopup"
              class="text-gray-500 hover:text-gray-700 transition-colors"
              title="Close"
            >
              <i class="fas fa-times text-lg"></i>
            </button>
          </div>
        </div>

        <!-- Modal Content -->
        <div class="p-6 overflow-y-auto max-h-[60vh]">
          <div v-if="isLoadingAllLabels" class="flex items-center justify-center py-8">
            <i class="fas fa-spinner fa-spin text-2xl text-gray-400 mr-3"></i>
            <span class="text-gray-600">Loading labels...</span>
          </div>
          
          <div v-else-if="allLabels.length === 0" class="text-center py-8">
            <i class="fas fa-tags text-4xl text-gray-300 mb-4"></i>
            <p class="text-gray-600">No labels available</p>
          </div>
          
          <div v-else class="space-y-3">
            <div
              v-for="label in allLabels"
              :key="label.id"
              class="flex items-center justify-between p-3 rounded-lg transition-colors"
              :class="getLabelClass(label)"
              @click="canToggleLabel(label) && toggleLabelAssignment(label)"
            >
              <div class="flex items-center space-x-3">
                <div 
                  class="w-3 h-3 rounded-full"
                  :class="isLabelAssigned(label) ? 'bg-green-500' : 'bg-blue-500'"
                ></div>
                <div>
                  <span class="font-medium text-gray-800">{{ label.label }}</span>
                  <span class="text-sm text-gray-500 ml-2">({{ label.slug }})</span>
                </div>
              </div>
              <div class="flex items-center space-x-2">
                <span 
                  v-if="isLabelAssigned(label)"
                  class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full"
                >
                  <i class="fas fa-check mr-1"></i>
                  Click to remove
                </span>
                <span 
                  v-else-if="isLabelAssignedToOtherTeam(label)"
                  class="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full"
                >
                  <i class="fas fa-users mr-1"></i>
                  {{ getOtherTeamName(label) }}
                </span>
                <span 
                  v-else
                  class="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full"
                >
                  <i class="fas fa-plus mr-1"></i>
                  Click to assign
                </span>
                <i 
                  v-if="isUpdatingLabel === label.id"
                  class="fas fa-spinner fa-spin text-gray-400"
                ></i>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="p-6 border-t border-gray-200 bg-gray-50">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-600">
              <span class="font-medium">{{ assignedLabelsCount }}</span> labels assigned to this player
            </div>
            <button
              @click="closeLabelsPopup"
              class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Player Games Modal -->
  <div v-if="showPlayerGamesModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div @click="closePlayerGamesModal" class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full">
        <!-- Header -->
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
              Last 3 Games for Team Players
            </h3>
            <button @click="closePlayerGamesModal" class="text-gray-400 hover:text-gray-500 focus:outline-none">
              <span class="sr-only">Close</span>
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="bg-gray-50 px-4 py-5 sm:p-6 max-h-[70vh] overflow-y-auto">
          <div v-if="playerGamesData.length === 0" class="text-center py-8 text-gray-500">
            No games found for players on this roster.
          </div>
          <div v-else class="space-y-6">
            <div v-for="playerData in playerGamesData" :key="playerData.player_id" class="bg-white shadow rounded-lg overflow-hidden">
              <div class="px-4 py-3 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
                <h4 class="font-medium text-gray-800">{{ playerData.player_name }}</h4>
                <div class="flex items-center space-x-2">
                  <span class="text-xs font-medium text-gray-500 uppercase">Status:</span>
                  <select 
                    v-model="playerData.selected_status_id"
                    class="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option :value="1">Active</option>
                    <option :value="2">Questionable</option>
                    <option :value="3">Out</option>
                    <option :value="4">Probable</option>
                    <option :value="5">Doubtful</option>
                  </select>
                </div>
              </div>
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Game id</th>
                      <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player id</th>
                      <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player Name</th>
                      <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Home</th>
                      <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Away</th>
                      <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MIN</th>
                      <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PTS</th>
                      <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">REB</th>
                      <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AST</th>
                      <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">STL</th>
                      <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">BLK</th>
                      <th scope="col" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">+/-</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="game in playerData.games" :key="game.id" class="hover:bg-gray-50">
                      <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {{ game.game_id }}
                      </td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {{ game.player_id }}
                      </td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {{ game.player_name }}
                      </td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {{ game.game_date ? game.game_date.split(' ')[0] : 'N/A' }}
                      </td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{{ game.team }}</td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {{ game.opponent_team_name || game.opponent_team || 'N/A' }}
                      </td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500" :title="game.data_note || ''">{{ game.minutes }}<span v-if="game.is_unknown" class="text-amber-600 ml-1" title="Participation unverified; automatic status unchanged">*</span></td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{{ game.points }}</td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{{ game.total_rebounds }}</td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{{ game.assists }}</td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{{ game.steals }}</td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{{ game.blocks }}</td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm" :class="parseInt(game.plus_minus) >= 0 ? 'text-green-600' : 'text-red-600'">
                        {{ game.plus_minus }}
                      </td>
                    </tr>
                    <tr v-if="!playerData.games || playerData.games.length === 0">
                      <td colspan="12" class="px-3 py-2 text-center text-sm text-gray-400 italic">No recent games</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
          <button 
            @click="confirmStatusesToDatabase" 
            type="button" 
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
            :disabled="isSaving"
          >
            <i v-if="isSaving" class="fas fa-spinner fa-spin mr-2"></i>
            Confirm Statuses to Database
          </button>
          <button @click="closePlayerGamesModal" type="button" class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>

  <RealGmTeamMappingModal :open="showPBTeamModal" :team="selectedTeam" @close="showPBTeamModal = false" @saved="onRealGmTeamMapped" />
</template>

<script>
import axios from '../axios.js'
import RealGmTeamMappingModal from './RealGmTeamMappingModal.vue'
import PlayerProfile from '@/components/PlayerProfile.vue'
import LeagueManagement from '@/components/LeagueManagement.vue'
import ProjectionLabelRow from '@/components/ProjectionLabelRow.vue'
import SeasonDetailsModal from '@/components/seasons/SeasonDetailsModal.vue'
import PlayerStatusDropdown from '@/components/PlayerStatusDropdown.vue'
import PlayerNewsModal from '@/components/PlayerNewsModal.vue'
import { useProjectionsStore } from '@/stores/projections'

export default {
  components: {
    RealGmTeamMappingModal,
    PlayerProfile,
    LeagueManagement,
    ProjectionLabelRow,
    SeasonDetailsModal,
    PlayerStatusDropdown,
    PlayerNewsModal
  },
  name: 'AutomatedProjectionsProfiles',
  props: {
    isSidebarCollapsed: {
      type: Boolean,
      default: false
    }
  },
        data() {
        return {
          avgTimeModalPlayer: null,
          activeTab: 'leagues',
          isLoadingLabels: false,
          isSaving: false,
          // Season details modal state
          showSeasonDetailsModal: false,
          seasonDetailsData: {
            season: '',
            playerId: null
          },
      playerSearch: '',
      searchResults: [],
      isSearching: false,
      searchTimeout: null,
      labelSearchTimeout: null,
      teamSearchTimeout: null,

      selectedProfile: null,
      selectedPlayer: null,
      currentPlayerIndex: 0,
      isLoadingPlayer: false,
      labelSearch: '',
      labelResults: [],
      isSearchingLabels: false,
      selectedLabel: null,
      currentLabelIndex: 0,
      isLoadingLabel: false,
      teamSearch: '',
      teamResults: [],
      isSearchingTeams: false,
      selectedTeam: null,
      currentTeamIndex: 0,
      isLoadingTeam: false,
      depthChartData: null,
      isLoadingDepthChart: false,
      showPlayerSelectionModal: false,
      selectedCell: null,
      selectedPosition: null,
      selectedRole: null,
      
      // Labels popup properties
      showLabelsModal: false,
      allLabels: [],
      isLoadingAllLabels: false,
      currentPlayerAssignedLabels: [],
      currentPlayer: null,
      currentTeam: null,
      isUpdatingLabel: null,
      
      // Player search properties
      playerSearchQuery: '',
      playerSearchResults: [],
      isSearchingPlayers: false,
      
      // League search properties
      leagueSearchQuery: '',
      leagueSearchResults: [],
      isSearchingLeagues: false,
      
      // Projections search properties
      projectionsSearchQuery: '',
      projectionsSearchResults: [],
      isSearchingProjections: false,

      editingTeam: null,
      editingLabel: null,
      newTeam: '',
      newLabel: '',
      
      // Context menu properties
      showContextMenu: false,
      contextMenuPosition: { x: 0, y: 0 },
      contextMenuPlayer: null,
      
      // Player news modal properties
      showPlayerNewsModal: false,
      newsModalPlayer: null,
      playerNewsUnread: {}, // { player_id: true } for players with unread news
      
      // Position update modal properties
      showPositionModal: false,
      positionModalPlayer: null,
      editingPosition: '',

      // PB Team Search properties
      pbTeamSearch: '',
      pbTeamResults: [],
      isSearchingPBTeams: false,
      showPBTeamDropdown: false,
      showPBTeamModal: false,
      pbTeamSearchTimeout: null,
      selectedPBTeamName: '', // Store the selected PB team name for display
      selectedRgmTeamName: '', // Store the selected RGM team name for display
      pbTeamModalTab: 'teams', // 'teams' or 'games'
      connectedGames: [], // Games connected to the selected team
      isLoadingGames: false,
      gameSearchQuery: '',
      gameSearchResults: [],

      // Mapping Search properties
      activeMappingSearch: null, // 'pb_team_id', 'rgm_team_id', or 'fs_team_id'
      mappingSearchQuery: '',
      mappingSearchResults: [],
      isSearchingMappings: false,
      mappingSearchTimeout: null,

      // Mapping Search properties
      activeMappingSearch: null, // 'pb_team_id', 'rgm_team_id', or 'fs_team_id'
      mappingSearchQuery: '',
      mappingSearchResults: [],
      isSearchingMappings: false,
      mappingSearchTimeout: null,

      // Player Games Modal properties
      showPlayerGamesModal: false,
      playerGamesData: [],
      isLoadingPlayerGames: false,

      menuItems: [
        {
          id: 'players',
          name: 'Players',
          icon: 'fas fa-user',
          count: 12,
          description: 'Manage player-specific projection profiles'
        },
        {
          id: 'leagues',
          name: 'Leagues',
          icon: 'fas fa-trophy',
          count: 8,
          description: 'Configure league-wide projection settings'
        },
        {
          id: 'teams',
          name: 'Teams',
          icon: 'fas fa-users',
          count: 15,
          description: 'Set up team-based projection models'
        },

        {
          id: 'player_labels',
          name: 'Player Labels',
          icon: 'fas fa-tags',
          count: 6,
          description: 'Manage player classification labels'
        },
       
      ],
      playerProfiles: [
        {
          id: 1,
          name: 'Star Player Model',
          description: 'High-scoring players with consistent performance',
          lastUpdated: '2 hours ago',
          status: 'active'
        },
        {
          id: 2,
          name: 'Rookie Projections',
          description: 'New players with limited historical data',
          lastUpdated: '1 day ago',
          status: 'active'
        },
        {
          id: 3,
          name: 'Injury Recovery Model',
          description: 'Players returning from injuries',
          lastUpdated: '3 days ago',
          status: 'inactive'
        }
      ],
      leagueProfiles: [
        {
          id: 1,
          name: 'Premier League',
          description: 'English Premier League specific models',
          lastUpdated: '1 hour ago',
          status: 'active'
        },
        {
          id: 2,
          name: 'La Liga',
          description: 'Spanish league projection settings',
          lastUpdated: '4 hours ago',
          status: 'active'
        },
        {
          id: 3,
          name: 'Bundesliga',
          description: 'German league configurations',
          lastUpdated: '2 days ago',
          status: 'active'
        }
      ],
      teamProfiles: [
        {
          id: 1,
          name: 'Top Tier Teams',
          description: 'Elite teams with high performance expectations',
          lastUpdated: '30 minutes ago',
          status: 'active'
        },
        {
          id: 2,
          name: 'Mid-Table Teams',
          description: 'Average performing teams',
          lastUpdated: '1 hour ago',
          status: 'active'
        },
        {
          id: 3,
          name: 'Underdog Teams',
          description: 'Teams with lower expectations',
          lastUpdated: '6 hours ago',
          status: 'active'
        }
      ],
      labelProjectionData: [
        {
          name: 'Returning',
          minutesData: {
            0: { lastSeason: 1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 }
          },
          rows: [
            { games: '0', lastSeason: 1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
            { games: '1-5', lastSeason: 0.25, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.75, restOfSeason: 0 },
            { games: '6-10', lastSeason: 0.1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.75, restOfSeason: 0.15 },
            { games: '11-15', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.75, restOfSeason: 0.25 },
            { games: '16-20', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.7, restOfSeason: 0.3 },
            { games: '21+', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.65, restOfSeason: 0.35 }
          ]
        },
        {
          name: 'Multi-Year Returning',
          minutesData: {
            0: { lastSeason: 0.8, twoSeasonsAgo: 0.2, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 }
          },
          rows: [
            { games: '0', lastSeason: 0.8, twoSeasonsAgo: 0.2, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
            { games: '1-5', lastSeason: 0.2, twoSeasonsAgo: 0.1, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.7, restOfSeason: 0 },
            { games: '6-10', lastSeason: 0.1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.75, restOfSeason: 0.15 },
            { games: '11-15', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.75, restOfSeason: 0.25 },
            { games: '16-20', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.7, restOfSeason: 0.3 },
            { games: '21+', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.65, restOfSeason: 0.35 }
          ]
        },
        {
          name: 'Same League (New Team)',
          minutesData: {
            0: { lastSeason: 1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 }
          },
          rows: [
            { games: '0', lastSeason: 1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
            { games: '1-5', lastSeason: 0.25, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.75, restOfSeason: 0 },
            { games: '6-10', lastSeason: 0.1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.75, restOfSeason: 0.15 },
            { games: '11-15', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.75, restOfSeason: 0.25 },
            { games: '16-20', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.7, restOfSeason: 0.3 },
            { games: '21+', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.65, restOfSeason: 0.35 }
          ]
        },
        {
          name: 'from Higher Comp',
          minutesData: {
            0: { lastSeason: 1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 }
          },
          rows: [
            { games: '0', lastSeason: 1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
            { games: '1-5', lastSeason: 0.2, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.8, restOfSeason: 0 },
            { games: '6-10', lastSeason: 0.1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.75, restOfSeason: 0.15 },
            { games: '11-15', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.75, restOfSeason: 0.25 },
            { games: '16-20', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.7, restOfSeason: 0.3 },
            { games: '21+', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.65, restOfSeason: 0.35 }
          ]
        },
        {
          name: 'from Lower Comp',
          minutesData: {
            0: { lastSeason: 1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 }
          },
          rows: [
            { games: '0', lastSeason: 1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
            { games: '1-5', lastSeason: 0.2, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.8, restOfSeason: 0 },
            { games: '6-10', lastSeason: 0.1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.75, restOfSeason: 0.15 },
            { games: '11-15', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.75, restOfSeason: 0.25 },
            { games: '16-20', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.7, restOfSeason: 0.3 },
            { games: '21+', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.65, restOfSeason: 0.35 }
          ]
        },
        {
          name: 'Rookie',
          minutesData: {
            0: { lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 1, lastFiveGames: 0, restOfSeason: 0 }
          },
          rows: [
            { games: '0', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 1, lastFiveGames: 0, restOfSeason: 0 },
            { games: '0-5', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.2, lastFiveGames: 0.8, restOfSeason: 0 },
            { games: '6-10', lastSeason: 0.1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.75, restOfSeason: 0.15 },
            { games: '11-15', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.75, restOfSeason: 0.25 },
            { games: '16-20', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.7, restOfSeason: 0.3 },
            { games: '21+', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.65, restOfSeason: 0.35 }
          ]
        },
        {
          name: 'Manual',
          minutesData: {
            0: { lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 }
          },
          rows: [
            { games: '0', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
            { games: '0-5', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
            { games: '6-10', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
            { games: '11-15', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
            { games: '16-20', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
            { games: '21+', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 }
          ]
        }
      ],
      playerLabelMenu: 'projection_weights'
    }
  },

  computed: {
    activeTabData() {
      if (!this.menuItems || this.menuItems.length === 0) {
        return { name: 'Unknown Tab', description: 'No tab data available' }
      }
      return this.menuItems.find(item => item.id === this.activeTab) || this.menuItems[0] || { name: 'Unknown Tab', description: 'No tab data available' }
    },
    
    assignedLabelsCount() {
      return this.currentPlayerAssignedLabels.length
    }
  },
  
  watch: {
    activeTab(newTab) {
      if (newTab === 'player_labels') {
        this.fetchProjectionLabels()
      }
    },
    playerLabelMenu(newMenu) {
      if (this.activeTab === 'player_labels') {
        this.fetchProjectionLabels()
      }
    },
    selectedTeam: {
      handler(team) {
        if (team?.roster?.length) {
          this.fetchUnreadStatusForRoster()
        } else {
          this.playerNewsUnread = {}
        }
      },
      deep: true
    }
  },
  
  computed: {
    showPopup: {
      get() {
        return useProjectionsStore().autoProjectionPopup
      },
      set(value) {
        useProjectionsStore().autoProjectionPopup = value
      }
    },
    teamTotalsResults() {
      return this.projectionsSearchResults.filter(result => 
        result.data_type === 'team_totals'
      )
    },
    opponentTotalsResults() {
      return this.projectionsSearchResults.filter(result => 
        result.data_type === 'opponent_totals'
      )
    }
  },
  mounted() {
    // Expose the open function globally so components can use it
    window.openSeasonDetailsModal = this.openSeasonDetailsModal
    
    // Add click listener to close context menu when clicking outside (use capture phase)
    document.addEventListener('click', this.handleClickOutside, true)
    // Add mousedown listener for better responsiveness
    document.addEventListener('mousedown', this.handleMouseDown, true)
    // Add escape key listener to close context menu
    document.addEventListener('keydown', this.handleEscapeKey)
  },
  
  beforeUnmount() {
    // Remove event listeners
    document.removeEventListener('click', this.handleClickOutside, true)
    document.removeEventListener('mousedown', this.handleMouseDown, true)
    document.removeEventListener('keydown', this.handleEscapeKey)
  },
  

  methods: {
    // Context menu methods
    async fetchLastThreeGamesForPlayerAllSelectedTeamPlayers() {
       if (!this.selectedTeam || !this.selectedTeam.id) {
        alert('Please select a team first.');
        return;
      }
      
      const teamId = this.selectedTeam.id;
      this.isLoadingPlayerGames = true; 
      
      try {
        const response = await axios.get(`/api/comparable-data-teams/${teamId}/player-games`);
        
        if (response.data.success) {
           this.playerGamesData = response.data.data.map(player => {
             // Suggestions come from the shared RealGM reader. Unknown evidence preserves status.
             const statusId = player.suggested_status_id ?? player.player_team_current_status ?? null;
             return {
               ...player,
               selected_status_id: statusId
             };
           });
           this.showPlayerGamesModal = true;
        } else {
           console.error('Failed to fetch games:', response.data.message);
           alert('Failed to fetch player games: ' + response.data.message);
        }
      } catch (error) {
        console.error('Error fetching player games:', error);
        alert('Error fetching player games.');
        } finally {
          this.isLoadingPlayerGames = false;
        }
      },



    hasUnreadNews(rosterItem) {
      const playerId = rosterItem?.player?.player_id ?? rosterItem?.player?.id
      return playerId && this.playerNewsUnread[String(playerId)] === true
    },
    async fetchUnreadStatusForRoster() {
      if (!this.selectedTeam?.roster?.length) return
      const playerIds = this.selectedTeam.roster
        .map(r => r?.player?.player_id ?? r?.player?.id)
        .filter(Boolean)
      if (playerIds.length === 0) return
      try {
        const response = await axios.post('/api/players/news/unread-status', {
          player_ids: [...new Set(playerIds)]
        })
        if (response.data?.success && response.data?.unread) {
          this.playerNewsUnread = { ...response.data.unread }
        }
      } catch (err) {
        console.error('Error fetching unread status:', err)
      }
    },
    shouldShowNo(rosterItem) {
      // Check if status is Out (3) or Doubtful (5)
      const statusId = rosterItem.player?.player_team_status?.player_status_id;
      if (statusId === 3 || statusId === 5) {
        return true;
      }
      
      // Check if ALL of L1, L2, L3 game minutes are below 10 (DNP counts as below 10)
      const lastGames = rosterItem.player?.lastGames || [];
      if (lastGames.length === 0) {
        return false;
      }
      
      // Check if all games have minutes below 10 (DNP is treated as below 10)
      return lastGames.every(game => {
        if (game.is_unknown) return false;
        // DNP counts as below 10
        if (game.is_dnp) {
          return true;
        }
        // Check if minutes are valid and below 10
        if (game.minutes !== undefined && game.minutes !== null && game.minutes !== '-') {
          return game.minutes < 10;
        }
        // If minutes are invalid/missing, treat as below 10
        return true;
      });
    },

    async fetchLastThreeGamesForAllPlayers() {
      if (!this.selectedTeam || !this.selectedTeam.id) {
        return;
      }
      
      const teamId = this.selectedTeam.id;
      console.log('Fetching games for team:', teamId, 'PB:', this.selectedTeam.pb_team_id, 'RGM:', this.selectedTeam.rgm_team_id, 'FS:', this.selectedTeam.fs_team_id);
      
      try {
        const response = await axios.get(`/api/comparable-data-teams/${teamId}/player-games`);
        console.log('Games response:', response.data);
        
        if (response.data.success && response.data.data) {
          // Map games data to roster items by player_id and player_name
          const gamesByPlayerId = {};
          const gamesByPlayerName = {};
          response.data.data.forEach(player => {
            if (player.player_id && player.games) {
              gamesByPlayerId[player.player_id] = player.games.slice(0, 3); // Get last 3 games
            }
            if (player.player_name && player.games) {
              gamesByPlayerName[player.player_name] = player.games.slice(0, 3);
            }
          });
          
          // Update roster items with games data
          if (this.selectedTeam.roster && Array.isArray(this.selectedTeam.roster)) {
            this.selectedTeam.roster.forEach(rosterItem => {
              if (!rosterItem.player) {
                return;
              }
              
              let playerGames = [];
              
              // Try to match by player ID first
              if (rosterItem.player.id) {
                playerGames = gamesByPlayerId[rosterItem.player.id] || [];
              }
              
              // Fallback to matching by player name
              if (playerGames.length === 0 && rosterItem.player.player_name) {
                playerGames = gamesByPlayerName[rosterItem.player.player_name] || [];
              }
              
              // Also try matching by rosterItem.id as a fallback
              if (playerGames.length === 0 && rosterItem.id) {
                playerGames = gamesByPlayerId[rosterItem.id] || [];
              }
              
              // Set lastGames on the player object to match API structure
              if (!rosterItem.player.lastGames) {
                this.$set(rosterItem.player, 'lastGames', playerGames);
              } else {
                rosterItem.player.lastGames = playerGames;
              }
            });
          }
        } else {
          // If no data, set empty arrays for all players to clear loading state
          if (this.selectedTeam.roster && Array.isArray(this.selectedTeam.roster)) {
            this.selectedTeam.roster.forEach(rosterItem => {
              if (rosterItem.player) {
                if (!rosterItem.player.lastGames) {
                  this.$set(rosterItem.player, 'lastGames', []);
                } else {
                  rosterItem.player.lastGames = [];
                }
              }
            });
          }
        }
      } catch (error) {
        console.error('Error fetching player games:', error);
        console.error('Error details:', error.response?.data || error.message);
        
        // Set empty arrays for all players to clear loading state even on error
        if (this.selectedTeam.roster && Array.isArray(this.selectedTeam.roster)) {
          this.selectedTeam.roster.forEach(rosterItem => {
            if (rosterItem.player) {
              if (!rosterItem.player.lastGames) {
                this.$set(rosterItem.player, 'lastGames', []);
              } else {
                rosterItem.player.lastGames = [];
              }
            }
          });
        }
      }
    },

    async confirmStatusesToDatabase() {
      if (!this.selectedTeam || !this.selectedTeam.id) return;
      
      this.isSaving = true;
      try {
        const payload = {
          team_id: this.selectedTeam.id,
          statuses: this.playerGamesData.filter(player => player.selected_status_id != null).map(player => ({
            player_id: player.player_id,
            player_status_id: player.selected_status_id
          }))
        };
        
        const response = await axios.post('/api/comparable-data-teams/save-player-statuses', payload);
        
        if (response.data.success) {
          // Apply exactly the confirmed statuses to the visible roster after a successful save.
          if (this.selectedTeam?.id === payload.team_id) {
            const saved = new Map(payload.statuses.map(row => [Number(row.player_id), row.player_status_id]));
            for (const row of this.selectedTeam.roster || []) {
              if (row.player && saved.has(Number(row.player.id))) {
                row.player.player_team_status = {
                  ...row.player.player_team_status,
                  player_status_id: saved.get(Number(row.player.id))
                };
              }
            }
          }
          alert('Statuses confirmed and saved to database successfully!');
          this.closePlayerGamesModal();
        } else {
          alert('Failed to save statuses: ' + response.data.message);
        }
      } catch (error) {
        console.error('Error saving statuses:', error);
        alert('Error saving statuses to database.');
      } finally {
        this.isSaving = false;
      }
    },

    openAvgTimeModal(player) {
      if (player) this.avgTimeModalPlayer = player
    },

    closeAvgTimeModal() {
      this.avgTimeModalPlayer = null
    },

    // Games behind "Avg Time", grouped by league: played games count, DNP / not in box score do not.
    avgTimeLeagueGroups(stats) {
      const groups = {}
      for (const game of stats?.list || []) {
        const name = game.league || (game.source === 'RealGM' ? 'Unknown league' : `${game.source} (no league, e.g. pre-season)`)
        const group = groups[name] || (groups[name] = { name, played: 0, minutes: 0, total: 0 })
        group.total++
        if (game.status === 'played') {
          group.played++
          group.minutes += game.minutes
        }
      }
      return Object.values(groups).map(g => ({ ...g, avg: g.played ? (g.minutes / g.played).toFixed(2) : '—' }))
    },

    closePlayerGamesModal() {
      this.showPlayerGamesModal = false;
      this.playerGamesData = [];
    },

    handleRightClick(event, rosterItem) {
      this.contextMenuPosition = {
        x: event.clientX,
        y: event.clientY
      }
      this.contextMenuPlayer = rosterItem
      this.showContextMenu = true
    },
    
    handleClickOutside(event) {
      // Close context menu if clicking outside of it
      if (!this.showContextMenu) return
      
      const contextMenu = document.querySelector('.context-menu-player')
      if (contextMenu && !contextMenu.contains(event.target)) {
        this.closeContextMenu()
      }
    },
    
    handleMouseDown(event) {
      // Close context menu if clicking outside of it (mousedown for better responsiveness)
      if (!this.showContextMenu) return
      
      const contextMenu = document.querySelector('.context-menu-player')
      if (contextMenu && !contextMenu.contains(event.target)) {
        this.closeContextMenu()
      }
    },
    
    handleEscapeKey(event) {
      // Close context menu on Escape key
      if (event.key === 'Escape' && this.showContextMenu) {
        this.closeContextMenu()
      }
    },
    
    closeContextMenu() {
      this.showContextMenu = false
      this.contextMenuPlayer = null
    },
    
    handleContextMenuAction(action) {
      if (!this.contextMenuPlayer) return
      
      switch (action) {
        case 'view-profile':
          // You can add view profile functionality here
          console.log('View profile for:', this.contextMenuPlayer.player?.player_name)
          break
        case 'manage-labels':
          if (this.contextMenuPlayer.player) {
            this.showLabelsPopup(
              this.contextMenuPlayer.player.assigned_labels || [],
              this.contextMenuPlayer.player,
              this.selectedTeam
            )
          }
          break
        case 'position':
          this.openPositionModal(this.contextMenuPlayer)
          break
        case 'news':
          this.openPlayerNewsModal(this.contextMenuPlayer)
          break
        case 'remove':
          this.removePlayerFromRoster(this.contextMenuPlayer)
          break
      }
      
      this.closeContextMenu()
    },
    
    openPlayerNewsModal(rosterItem) {
      if (rosterItem && rosterItem.player) {
        // Set the player data first
        this.newsModalPlayer = {
          id: rosterItem.player.player_id || rosterItem.player.id,
          name: rosterItem.player.player_name || 'Unknown Player',
          status: rosterItem.player.player_status,
          rosterItemId: rosterItem.id
        }
        // Use nextTick to ensure the component is created before setting show to true
        this.$nextTick(() => {
          this.showPlayerNewsModal = true
        })
      }
    },
    
    closePlayerNewsModal() {
      this.showPlayerNewsModal = false
      this.newsModalPlayer = null
      this.fetchUnreadStatusForRoster()
    },
    
    openPositionModal(rosterItem) {
      if (rosterItem && rosterItem.player) {
        this.positionModalPlayer = {
          id: rosterItem.player.player_id || rosterItem.player.id,
          name: rosterItem.player.player_name || 'Unknown Player',
          currentPosition: rosterItem.player.position || ''
        }
        this.editingPosition = rosterItem.player.position || ''
        this.showPositionModal = true
      }
    },
    
    closePositionModal() {
      this.showPositionModal = false
      this.positionModalPlayer = null
      this.editingPosition = ''
    },
    
    async savePlayerPosition() {
      if (!this.positionModalPlayer || !this.editingPosition.trim()) {
        return
      }
      
      try {
        const playerId = this.positionModalPlayer.id
        const newPosition = this.editingPosition.trim()
        
        // Update player position via API
        const response = await axios.put(`/api/players/${playerId}/position`, {
          position: newPosition
        })
        
        if (response.data.success) {
          // Update the local player object
          const rosterItem = this.selectedTeam.roster.find(item => {
            const itemPlayerId = item.player?.player_id || item.player?.id
            return itemPlayerId === playerId
          })
          
          if (rosterItem && rosterItem.player) {
            rosterItem.player.position = newPosition
          }
          
          this.closePositionModal()
        } else {
          alert('Failed to update player position: ' + (response.data.message || 'Unknown error'))
        }
      } catch (error) {
        console.error('Error updating player position:', error)
        alert('Failed to update player position: ' + (error.response?.data?.error || error.message))
      }
    },
    
    handleNewsModalStatusUpdate(data) {
      // Update the player status in the roster item
      const rosterItem = this.selectedTeam.roster.find(item => item.id === data.rosterItemId)
      if (rosterItem && rosterItem.player) {
        rosterItem.player.player_status = data.statusId
      }
      
      // Update the newsModalPlayer status if modal is open to keep it in sync
      if (this.newsModalPlayer && this.newsModalPlayer.rosterItemId === data.rosterItemId) {
        this.newsModalPlayer.status = data.statusId
      }
    },

    
    getMinutesData(label, key, property) {
      if (!label.minutesData) {
        label.minutesData = {}
      }
      if (!label.minutesData[key]) {
        label.minutesData[key] = {
          lastSeason: 0,
          twoSeasonsAgo: 0,
          threeSeasonsAgo: 0,
          leagueAvg: 0,
          lastFiveGames: 0,
          restOfSeason: 0
        }
      }
      return label.minutesData[key][property]
    },
    
    updateMinutesData(label, key, property, value) {
      this.getMinutesData(label, key, property) // Ensure data exists
      label.minutesData[key][property] = parseFloat(value) || 0
    },
    
    async searchPBTeams() {
      clearTimeout(this.pbTeamSearchTimeout);
      this.pbTeamSearchTimeout = setTimeout(async () => {
        if (this.pbTeamSearch.length < 2) {
          this.pbTeamResults = [];
          return;
        }
        this.isSearchingPBTeams = true;
        try {
          const response = await axios.get('/api/comparable-data-teams/search', {
            params: { search: this.pbTeamSearch }
          });
          this.pbTeamResults = response.data.data || [];
          this.showPBTeamDropdown = true;
        } catch (error) {
          console.error('Error searching PB teams:', error);
          this.pbTeamResults = [];
        } finally {
          this.isSearchingPBTeams = false;
        }
      }, 300);
    },

    async searchMappings() {
      if (!this.activeMappingSearch) return;
      
      clearTimeout(this.mappingSearchTimeout);
      this.mappingSearchTimeout = setTimeout(async () => {
        if (this.mappingSearchQuery.length < 2) {
          this.mappingSearchResults = [];
          return;
        }
        
        this.isSearchingMappings = true;
        try {
          const response = await axios.get('/api/comparable-data-teams/search', {
            params: { search: this.mappingSearchQuery }
          });
          this.mappingSearchResults = response.data.data || response.data || [];
        } catch (error) {
          console.error('Error searching mappings:', error);
          this.mappingSearchResults = [];
        } finally {
          this.isSearchingMappings = false;
        }
      }, 300);
    },

    async selectMapping(result) {
      if (!this.selectedTeam || !this.activeMappingSearch) return;

      try {
        const updateData = {
          team_name: this.selectedTeam.team_name,
          league: this.selectedTeam.league,
          region: this.selectedTeam.region,
          league_id: this.selectedTeam.league_id
        };

        // Update the appropriate field based on which mapping type was selected
        if (this.activeMappingSearch === 'pb_team_id') {
          updateData.pb_team_id = String(result.team_id || result.id || '');
          updateData.rgm_team_id = this.selectedTeam.rgm_team_id ? String(this.selectedTeam.rgm_team_id) : null;
          updateData.fs_team_id = this.selectedTeam.fs_team_id ? String(this.selectedTeam.fs_team_id) : null;
        } else if (this.activeMappingSearch === 'rgm_team_id') {
          updateData.rgm_team_id = String(result.team_id || result.id || '');
          updateData.pb_team_id = this.selectedTeam.pb_team_id ? String(this.selectedTeam.pb_team_id) : null;
          updateData.fs_team_id = this.selectedTeam.fs_team_id ? String(this.selectedTeam.fs_team_id) : null;
        } else if (this.activeMappingSearch === 'fs_team_id') {
          // For FS, use team_id_string (varchar) as the identifier
          updateData.fs_team_id = String(result.team_id_string || result.team_id || result.id || '');
          updateData.pb_team_id = this.selectedTeam.pb_team_id ? String(this.selectedTeam.pb_team_id) : null;
          updateData.rgm_team_id = this.selectedTeam.rgm_team_id ? String(this.selectedTeam.rgm_team_id) : null;
        }

        await axios.put(`/api/team-data-management/teams/${this.selectedTeam.id}`, updateData);

        // Update local state
        if (this.activeMappingSearch === 'pb_team_id') {
          this.selectedTeam.pb_team_id = String(result.team_id || result.id || '');
        } else if (this.activeMappingSearch === 'rgm_team_id') {
          this.selectedTeam.rgm_team_id = String(result.team_id || result.id || '');
        } else if (this.activeMappingSearch === 'fs_team_id') {
          this.selectedTeam.fs_team_id = String(result.team_id_string || result.team_id || result.id || '');
        }

        // Clear search
        this.activeMappingSearch = null;
        this.mappingSearchQuery = '';
        this.mappingSearchResults = [];
      } catch (error) {
        console.error('Error updating mapping:', error);
        alert('Failed to update mapping');
      }
    },

    async removeMapping(mappingType) {
      if (!this.selectedTeam || !mappingType) return;

      const labels = { pb_team_id: 'PB Team ID', rgm_team_id: 'RGM Team ID', fs_team_id: 'FS Team ID' };
      if (!confirm(`Are you sure you want to remove the ${labels[mappingType] || mappingType} mapping?`)) {
        return;
      }

      try {
        const updateData = {
          team_name: this.selectedTeam.team_name,
          league: this.selectedTeam.league,
          region: this.selectedTeam.region,
          league_id: this.selectedTeam.league_id,
          pb_team_id: mappingType === 'pb_team_id' ? null : (this.selectedTeam.pb_team_id ? String(this.selectedTeam.pb_team_id) : null),
          rgm_team_id: mappingType === 'rgm_team_id' ? null : (this.selectedTeam.rgm_team_id ? String(this.selectedTeam.rgm_team_id) : null),
          fs_team_id: mappingType === 'fs_team_id' ? null : (this.selectedTeam.fs_team_id ? String(this.selectedTeam.fs_team_id) : null)
        };

        await axios.put(`/api/team-data-management/teams/${this.selectedTeam.id}`, updateData);

        // Update local state
        if (mappingType === 'pb_team_id') {
          this.selectedTeam.pb_team_id = null;
        } else if (mappingType === 'rgm_team_id') {
          this.selectedTeam.rgm_team_id = null;
        } else if (mappingType === 'fs_team_id') {
          this.selectedTeam.fs_team_id = null;
        }
      } catch (error) {
        console.error('Error removing mapping:', error);
        alert('Failed to remove mapping');
      }
    },
    
    async selectPBTeam(team) {
      if (!this.selectedTeam || !this.selectedTeam.id) {
        console.error('No team selected or team ID missing');
        return;
      }
      
      try {
        // Update the team's pb_team_id in the database
        const response = await axios.put(`/api/team-data-management/teams/${this.selectedTeam.id}`, {
          pb_team_id: team.team_id,
          team_name: this.selectedTeam.team_name,
          league: this.selectedTeam.league,
          region: this.selectedTeam.region,
          league_id: this.selectedTeam.league_id,
          rgm_team_id: this.selectedTeam.rgm_team_id,
          fs_team_id: this.selectedTeam.fs_team_id
        });

        // Update local state
        this.selectedTeam.pb_team_id = team.team_id;
        this.selectedPBTeamName = team.team;
        
        // Refresh the team list to show updated connection
        await this.searchPBTeams();
        
        console.log('PB Team ID saved successfully:', response.data);
      } catch (error) {
        console.error('Error saving PB Team ID:', error);
        alert('Failed to save PB Team ID. Please try again.');
      }
    },

    async selectRgmTeam(team) {
      if (!this.selectedTeam || !this.selectedTeam.id) {
        console.error('No team selected or team ID missing');
        return;
      }
      
      try {
        // Update the team's rgm_team_id in the database
        const response = await axios.put(`/api/team-data-management/teams/${this.selectedTeam.id}`, {
          rgm_team_id: team.team_id,
          team_name: this.selectedTeam.team_name,
          league: this.selectedTeam.league,
          region: this.selectedTeam.region,
          league_id: this.selectedTeam.league_id,
          pb_team_id: this.selectedTeam.pb_team_id,
          fs_team_id: this.selectedTeam.fs_team_id
        });

        // Update local state
        this.selectedTeam.rgm_team_id = team.team_id;
        this.selectedRgmTeamName = team.team;
        
        // Refresh the team list to show updated connection
        await this.searchPBTeams();
        
        console.log('RGM Team ID saved successfully:', response.data);
      } catch (error) {
        console.error('Error saving RGM Team ID:', error);
        alert('Failed to save RGM Team ID. Please try again.');
      }
    },

    async disconnectTeam(team) {
      if (!this.selectedTeam || !this.selectedTeam.id) {
        return;
      }
      
      if (!confirm(`Are you sure you want to disconnect ${team.team}?`)) {
        return;
      }
      
      try {
        const updateData = {
          team_name: this.selectedTeam.team_name,
          league: this.selectedTeam.league,
          region: this.selectedTeam.region,
          league_id: this.selectedTeam.league_id
        };
        
        if (team.team_type === 'pb') {
          updateData.pb_team_id = null;
          this.selectedTeam.pb_team_id = null;
          this.selectedPBTeamName = '';
        } else if (team.team_type === 'fs') {
          updateData.fs_team_id = null;
          this.selectedTeam.fs_team_id = null;
        } else {
          updateData.rgm_team_id = null;
          this.selectedTeam.rgm_team_id = null;
          this.selectedRgmTeamName = '';
        }
        
        const response = await axios.put(`/api/team-data-management/teams/${this.selectedTeam.id}`, updateData);
        
        // Refresh the team list
        await this.searchPBTeams();
        
        console.log('Team disconnected successfully:', response.data);
      } catch (error) {
        console.error('Error disconnecting team:', error);
        alert('Failed to disconnect team. Please try again.');
      }
    },

    async loadConnectedGames() {
      if (!this.selectedTeam || (!this.selectedTeam.pb_team_id && !this.selectedTeam.rgm_team_id && !this.selectedTeam.fs_team_id)) {
        this.connectedGames = [];
        return;
      }

      this.isLoadingGames = true;
      try {
        // Fetch games from comparable_data for pb_team_id, rgm_team_id, and fs_team_id
        const teamIds = [];
        if (this.selectedTeam.pb_team_id) teamIds.push(this.selectedTeam.pb_team_id);
        if (this.selectedTeam.rgm_team_id) teamIds.push(this.selectedTeam.rgm_team_id);
        if (this.selectedTeam.fs_team_id) teamIds.push(this.selectedTeam.fs_team_id);
        
        // You'll need to create an API endpoint for this or use existing one
        // For now, using a placeholder approach
        const response = await axios.get('/api/comparable-data-teams/search', {
          params: { search: '' }
        });
        
        // This is a placeholder - you'll need to create proper endpoint
        // to fetch games by team_id from comparable_data
        this.connectedGames = []; // Will be populated by proper API call
        
      } catch (error) {
        console.error('Error loading connected games:', error);
        this.connectedGames = [];
      } finally {
        this.isLoadingGames = false;
      }
    },

    async searchGames() {
      // Placeholder for game search functionality
      // You'll need to create an API endpoint to search games
      if (this.gameSearchQuery.length < 2) {
        this.gameSearchResults = [];
        return;
      }
      
      // This is a placeholder - implement actual game search
      this.gameSearchResults = [];
    },

    async addGame(game) {
      // Placeholder for adding game functionality
      // You'll need to create an API endpoint to add games
      try {
        // Add game to connected games
        if (!this.connectedGames.find(g => g.id === game.id)) {
          this.connectedGames.push(game);
        }
      } catch (error) {
        console.error('Error adding game:', error);
        alert('Failed to add game. Please try again.');
      }
    },

    async removeGame(game) {
      // Remove game from connected games
      this.connectedGames = this.connectedGames.filter(g => g.id !== game.id);
      // You'll need to create an API endpoint to remove the connection
    },
    
    closePBTeamDropdown() {
      // Small delay to allow click events on dropdown items to fire
      setTimeout(() => {
        this.showPBTeamDropdown = false;
      }, 200);
    },

    async fetchPBTeamName(pbTeamId) {
      try {
        // Search for the team by team_id to get the team name
        // PB teams have source = null, 0, or 1 (not 2)
        const response = await axios.get('/api/comparable-data-teams/search', {
          params: { search: '' } // Empty search to get all teams
        });
        
        if (response.data.success && response.data.data) {
          // Find team with matching team_id and source != 2 (PB team)
          const team = response.data.data.find(t => 
            t.team_id == pbTeamId && 
            (t.source == null || t.source == 0 || t.source == 1 || t.source != 2)
          );
          if (team) {
            this.selectedPBTeamName = team.team;
          } else {
            // Fallback: try without source filter
            const fallbackTeam = response.data.data.find(t => t.team_id == pbTeamId);
            if (fallbackTeam) {
              this.selectedPBTeamName = fallbackTeam.team;
            } else {
              this.selectedPBTeamName = `Team ID: ${pbTeamId}`;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching PB team name:', error);
        this.selectedPBTeamName = `Team ID: ${pbTeamId}`;
      }
    },

    async fetchRgmTeamName(rgmTeamId) {
      try {
        // Search for the team by team_id to get the team name
        // RGM teams have source = 2
        const response = await axios.get('/api/comparable-data-teams/search', {
          params: { search: '' } // Empty search to get all teams
        });
        
        if (response.data.success && response.data.data) {
          // Find team with matching team_id and source = 2 (RGM team)
          const team = response.data.data.find(t => 
            t.team_id == rgmTeamId && t.source == 2
          );
          if (team) {
            this.selectedRgmTeamName = team.team;
          } else {
            // Fallback: try without source filter
            const fallbackTeam = response.data.data.find(t => t.team_id == rgmTeamId);
            if (fallbackTeam) {
              this.selectedRgmTeamName = fallbackTeam.team;
            } else {
              this.selectedRgmTeamName = `Team ID: ${rgmTeamId}`;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching RGM team name:', error);
        this.selectedRgmTeamName = `Team ID: ${rgmTeamId}`;
      }
    },

    isTeamLinkedToCurrentTeam(team) {
      if (!this.selectedTeam) return false;
      
      // Check if this team is linked to the current selectedTeam
      if (team.team_type === 'rgm') {
        return this.selectedTeam.rgm_team_id == team.team_id;
      } else if (team.team_type === 'fs') {
        return this.selectedTeam.fs_team_id == team.team_id_string;
      } else {
        return this.selectedTeam.pb_team_id == team.team_id;
      }
    },

    openPBTeamModal() {
      this.showPBTeamModal = true;
    },
    async onRealGmTeamMapped(name) {
      if (!this.selectedTeam) return;
      this.selectedTeam.realgm_team_name = name;
      await this.fetchLastThreeGamesForAllPlayers();
    },

    closePopup() {
      useProjectionsStore().autoProjectionPopup = false
    },
    
    // Labels popup methods
    async showLabelsPopup(assignedLabels, player, team) {
      this.currentPlayerAssignedLabels = assignedLabels || []
      this.currentPlayer = player
      this.currentTeam = team
      this.showLabelsModal = true
      
      // Fetch all available labels if not already loaded
      if (this.allLabels.length === 0) {
        await this.fetchAllLabels()
      }
    },
    
    closeLabelsPopup() {
      this.showLabelsModal = false
      this.currentPlayerAssignedLabels = []
      this.currentPlayer = null
      this.currentTeam = null
      this.isUpdatingLabel = null
    },
    
    async fetchAllLabels() {
      this.isLoadingAllLabels = true
      try {
        const response = await axios.get('/api/v2/projections-settings/all-player-label-names')
        this.allLabels = response.data || []
      } catch (error) {
        console.error('Error fetching all labels:', error)
        this.allLabels = []
      } finally {
        this.isLoadingAllLabels = false
      }
    },
    
    isLabelAssigned(label) {
      if (!this.currentPlayerAssignedLabels || this.currentPlayerAssignedLabels.length === 0) {
        return false
      }
      
      return this.currentPlayerAssignedLabels.some(assignedLabel => {
        const assignedLabelName = assignedLabel.player_label?.label || assignedLabel.player_label?.name || assignedLabel.player_label?.label_name
        const isCurrentTeam = assignedLabel.active_team_id === this.currentTeam.id
        return assignedLabelName === label.label && isCurrentTeam
      })
    },
    
    isLabelAssignedToOtherTeam(label) {
      if (!this.currentPlayerAssignedLabels || this.currentPlayerAssignedLabels.length === 0) {
        return false
      }
      
      return this.currentPlayerAssignedLabels.some(assignedLabel => {
        const assignedLabelName = assignedLabel.player_label?.label || assignedLabel.player_label?.name || assignedLabel.player_label?.label_name
        const isOtherTeam = assignedLabel.active_team_id !== this.currentTeam.id
        return assignedLabelName === label.label && isOtherTeam
      })
    },
    
    getOtherTeamName(label) {
      if (!this.currentPlayerAssignedLabels || this.currentPlayerAssignedLabels.length === 0) {
        return null
      }
      
      const assignedLabel = this.currentPlayerAssignedLabels.find(assignedLabel => {
        const assignedLabelName = assignedLabel.player_label?.label || assignedLabel.player_label?.name || assignedLabel.player_label?.label_name
        const isOtherTeam = assignedLabel.active_team_id !== this.currentTeam.id
        return assignedLabelName === label.label && isOtherTeam
      })
      
      return assignedLabel?.team?.team_name || assignedLabel?.team?.name || 'Other Team'
    },
    
    getLabelClass(label) {
      if (this.isLabelAssigned(label)) {
        return 'bg-green-50 hover:bg-green-100 border border-green-200 cursor-pointer'
      } else if (this.isLabelAssignedToOtherTeam(label)) {
        return 'bg-orange-50 border border-orange-200'
      } else {
        return 'bg-gray-50 hover:bg-gray-100 border border-gray-200 cursor-pointer'
      }
    },
    
    canToggleLabel(label) {
      // Can toggle if assigned to current team or not assigned at all
      return this.isLabelAssigned(label) || !this.isLabelAssignedToOtherTeam(label)
    },
    
    async toggleLabelAssignment(label) {
      if (!this.currentPlayer || !this.currentTeam) {
        console.error('Missing player or team information')
        return
      }
      
      this.isUpdatingLabel = label.id
      
      try {
        const isCurrentlyAssigned = this.isLabelAssigned(label)
        
        if (isCurrentlyAssigned) {
          // Remove the label assignment
          await this.removeLabelAssignment(label)
        } else {
          // Add the label assignment
          await this.addLabelAssignment(label)
        }
        
      } catch (error) {
        console.error('Error toggling label assignment:', error)
        alert('Failed to update label assignment. Please try again.')
      } finally {
        this.isUpdatingLabel = null
      }
    },
    
    async addLabelAssignment(label) {
      const requestData = {
        player_id: this.currentPlayer.id || this.currentPlayer.p_player_id || this.currentPlayer.player_id,
        team_data: {
          team_id: this.currentTeam.id,
          team_name: this.currentTeam.team_name || this.currentTeam.name
        },
        player_label_id: label.id
      }
      
      const response = await axios.post('/api/v2/projections-settings/player-labels/assigned-labels/save', requestData)
      
      // Update the current player data with the response
      if (response.data.updated_player) {
        this.currentPlayer = response.data.updated_player
        this.currentPlayerAssignedLabels = response.data.updated_player.assigned_labels || []
      }
      
      return response.data
    },
    
    async removeLabelAssignment(label) {
      const requestData = {
        player_id: this.currentPlayer.id || this.currentPlayer.p_player_id || this.currentPlayer.player_id,
        team_data: {
          team_id: this.currentTeam.id,
          team_name: this.currentTeam.team_name || this.currentTeam.name
        },
        player_label_id: label.id
      }
      
      const response = await axios.delete('/api/v2/projections-settings/player-labels/assigned-labels/remove', { data: requestData })
      
      // Update the current player data with the response
      if (response.data.updated_player) {
        this.currentPlayer = response.data.updated_player
        this.currentPlayerAssignedLabels = response.data.updated_player.assigned_labels || []
      }
      
      return response.data
    },
    
    
    // Season details modal handlers
    openSeasonDetailsModal(season, playerId) {
      this.seasonDetailsData = { season, playerId }
      this.showSeasonDetailsModal = true
    },
    
    closeSeasonDetailsModal() {
      this.showSeasonDetailsModal = false
      this.seasonDetailsData = { season: '', playerId: null }
    },
    refreshProfiles() {
      // Implement refresh logic
      console.log('Refreshing profiles for:', this.activeTab)
    },
    addNewProfile() {
      // Implement add new profile logic
      console.log('Adding new profile for:', this.activeTab)
    },
    async searchPlayers() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      // Set new timeout for debounced search
      this.searchTimeout = setTimeout(async () => {
        if (this.playerSearch.length < 2) {
          this.searchResults = []
          return
        }

        this.isSearching = true
        try {
          const response = await axios.get(`/api/v2/players/search/${encodeURIComponent(this.playerSearch)}`)
          this.searchResults = response.data
        } catch (error) {
          console.error('Error searching players:', error)
          this.searchResults = []
        } finally {
          this.isSearching = false
        }
      }, 300) // 300ms delay
    },
    clearPlayerSearch() {
      this.playerSearch = ''
      this.searchResults = []
    },
    
    // Player search methods for team roster
    async searchPlayersForRoster() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      // Set new timeout for debounced search
      this.searchTimeout = setTimeout(async () => {
        if (this.playerSearchQuery.length < 2) {
          this.playerSearchResults = []
          return
        }

        this.isSearchingPlayers = true
        try {
          const response = await axios.get(`/api/v2/players/search/${encodeURIComponent(this.playerSearchQuery)}`)
          this.playerSearchResults = response.data
        } catch (error) {
          console.error('Error searching players:', error)
          this.playerSearchResults = []
        } finally {
          this.isSearchingPlayers = false
        }
      }, 300) // 300ms delay
    },
    
    async addPlayerToRoster(player) {
      if (!this.selectedTeam) {
        alert('Please select a team first')
        return
      }
      
      try {
        // Check if player is already in roster
        const existingPlayer = this.selectedTeam.roster?.find(rosterItem => 
          rosterItem.player?.player_id === player.player_id
        )
        
        if (existingPlayer) {
          alert('Player is already in the roster')
          return
        }
        
        // Add player to roster (you'll need to implement the API call)
        console.log('Adding player to roster:', player, 'for team:', this.selectedTeam)
        
        // For now, just add to local state
        if (!this.selectedTeam.roster) {
          this.selectedTeam.roster = []
        }
        
        this.selectedTeam.roster.push({
          id: Date.now(), // Temporary ID
          player: player,
          player_status: 'active' // Default status
        })
        
        // Clear search
        this.playerSearchQuery = ''
        this.playerSearchResults = []
        
        alert(`Player "${player.player_name}" added to roster successfully!`)
      } catch (error) {
        console.error('Error adding player to roster:', error)
        alert('Failed to add player to roster')
      }
    },
    
    async removePlayerFromRoster(rosterItem) {
      if (confirm(`Are you sure you want to remove ${rosterItem.player?.player_name} from the roster?`)) {
        try {
          // Remove player from roster (you'll need to implement the API call)
          console.log('Removing player from roster:', rosterItem)
          
          // For now, just remove from local state
          const index = this.selectedTeam.roster.findIndex(item => item.id === rosterItem.id)
          if (index > -1) {
            this.selectedTeam.roster.splice(index, 1)
          }
          
          alert('Player removed from roster successfully!')
        } catch (error) {
          console.error('Error removing player from roster:', error)
          alert('Failed to remove player from roster')
        }
      }
    },
    
    // Projections search methods
    async searchProjectionsStats() {
      const searchTerm = this.projectionsSearchQuery
      if (!searchTerm || searchTerm.length < 2) {
        this.projectionsSearchResults = []
        return
      }
      
      this.isSearchingProjections = true
      
      // Simulate API call delay
      setTimeout(() => {
        // This would typically call an API endpoint
        // For now, we'll use mock data or filter existing projections data
        const results = [] // You would populate this with actual API results
        this.projectionsSearchResults = results
        this.isSearchingProjections = false
      }, 300)
    },
    
    addProjectionTeamToTeam(projectionTeam, currentTeam) {
      // Emit event to parent to handle adding projection team
      console.log('Adding projection team to team:', projectionTeam, 'for current team:', currentTeam)
      // You can implement the actual logic here
    },
    
    formatDataType(dataType) {
      if (!dataType) return 'Unknown Type'
      return dataType.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')
    },
    
    // League search methods
    async searchLeagues() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      // Set new timeout for debounced search
      this.searchTimeout = setTimeout(async () => {
        if (this.leagueSearchQuery.length < 2) {
          this.leagueSearchResults = []
          return
        }

        this.isSearchingLeagues = true
        try {
          const response = await axios.get(`/api/v2/leagues/search/${encodeURIComponent(this.leagueSearchQuery)}`)
          this.leagueSearchResults = response.data
        } catch (error) {
          console.error('Error searching leagues:', error)
          this.leagueSearchResults = []
        } finally {
          this.isSearchingLeagues = false
        }
      }, 300) // 300ms delay
    },
    
    async addLeagueToTeam(league) {
      if (!this.selectedTeam) {
        alert('Please select a team first')
        return
      }
      
      try {
        // Check if league is already in team
        const existingLeague = this.selectedTeam.leagues?.find(leagueItem => 
          leagueItem.id === league.id
        )
        
        if (existingLeague) {
          alert('League is already added to the team')
          return
        }
        
        // Add league to team
        console.log('Adding league to team:', league, 'for team:', this.selectedTeam)
        
        // For now, just add to local state
        if (!this.selectedTeam.leagues) {
          this.selectedTeam.leagues = []
        }
        
        this.selectedTeam.leagues.push({
          ...league,
          team_league_status: 'active' // Default status
        })
        
        // Clear search
        this.leagueSearchQuery = ''
        this.leagueSearchResults = []
        
        alert(`League "${league.league_name}" added to team successfully!`)
      } catch (error) {
        console.error('Error adding league to team:', error)
        alert('Failed to add league to team')
      }
    },
    
    async removeLeagueFromTeam(leagueItem) {
      if (confirm(`Are you sure you want to remove ${leagueItem.league_name} from the team?`)) {
        try {
          // Remove league from team
          console.log('Removing league from team:', leagueItem)
          
          // For now, just remove from local state
          const index = this.selectedTeam.leagues.findIndex(item => item.id === leagueItem.id)
          if (index > -1) {
            this.selectedTeam.leagues.splice(index, 1)
          }
          
          alert('League removed from team successfully!')
        } catch (error) {
          console.error('Error removing league from team:', error)
          alert('Failed to remove league from team')
        }
      }
    },
    
    async updateLeagueStatus(leagueItem) {
      try {
        console.log('Updating league status:', leagueItem.league_name, 'to:', leagueItem.team_league_status)
        
        // Here you would typically make an API call to update the league status
        // For now, we'll just log the change
        // await axios.put(`/api/v2/teams/${this.selectedTeam.id}/leagues/${leagueItem.id}/status`, {
        //   status: leagueItem.team_league_status
        // })
        
        // Show success message
        console.log(`League "${leagueItem.league_name}" status updated to "${leagueItem.team_league_status}"`)
      } catch (error) {
        console.error('Error updating league status:', error)
        alert('Failed to update league status')
      }
    },
    
    // Update player status
     async updatePlayerStatus(data) {
      let playerId, status;
      try {
        ({ playerId, status } = data);
        console.log('Updating player status:', playerId, 'to:', status)
        
        // Make API call to update player status using Laravel API
        const rosterItem = this.selectedTeam.roster.find(item => item.id === playerId)
        const actualPlayerId = rosterItem?.player?.player_id || rosterItem?.player?.id
        
        const response = await axios.put(`/api/v2/projections-players/teams/player/${playerId}/status`, {
          player_status: status,
          player_id: actualPlayerId,
          team_id: this.selectedTeam.id,
        })
        
        if (response.data.success) {
          console.log(`Player status updated successfully: ${response.data.message}`)
          console.log('Status label:', response.data.data?.status_label)
          
          // Update the local roster item with the response data
          const currentRosterItem = this.selectedTeam.roster.find(item => item.id === playerId)
          if (currentRosterItem && currentRosterItem.player && response.data.data) {
            const responseData = response.data.data;
            
            // Ensure player_team_status object exists
            if (!currentRosterItem.player.player_team_status) {
              currentRosterItem.player.player_team_status = {};
            }
            
            // Update the player_status_id from team_updated
            if (responseData.team_updated) {
               currentRosterItem.player.player_team_status.player_status_id = responseData.team_updated.player_status_id;
            }
            
            // Update the nested player_status object for immediate label change
            if (!currentRosterItem.player.player_team_status.player_status || typeof currentRosterItem.player.player_team_status.player_status !== 'object') {
              currentRosterItem.player.player_team_status.player_status = {};
            }
            
            currentRosterItem.player.player_team_status.player_status.id = responseData.player_status;
            currentRosterItem.player.player_team_status.player_status.name = responseData.status_label;
            
            console.log('Local state updated for player:', currentRosterItem.player.player_name, 'Status:', responseData.status_label);
          }
        }
      } catch (error) {
        console.error('Error updating player status:', error)
        alert('Failed to update player status: ' + (error.response?.data?.error || error.message))
        
        // Revert the status change in the UI if the API call failed
        if (playerId && this.selectedTeam && this.selectedTeam.roster) {
           const rosterItem = this.selectedTeam.roster.find(item => item.id === playerId)
           if (rosterItem && rosterItem.player) {
             // Revert or show error indicator
           }
        }
      }
    },
    
    // Fetch depth chart data for the selected team
    async fetchDepthChartData(teamId) {
      if (!teamId) {
        this.depthChartData = null
        return
      }
      
      this.isLoadingDepthChart = true
      try {
        const response = await axios.get(`/api/v2/projections-settings/teams/depth-chart/${teamId}`)
        console.log('Depth chart data:', response.data)
        
        // Check if response is empty or has no data
        if (!response.data || response.data.length === 0) {
          console.log('No depth chart data found, creating initial structure')
          
          // Create the full payload with all roles
          const initialDepthChartData = {
            'starter': {
              id: null,
              team_id: teamId,
              role: 'Starter',
              pg_players: null,
              sg_players: null,
              sf_players: null,
              pf_players: null,
              c_players: null,
              role_order: 1,
              is_active: 1
            },
            'rotational': {
              id: null,
              team_id: teamId,
              role: 'Rotational Player',
              pg_players: null,
              sg_players: null,
              sf_players: null,
              pf_players: null,
              c_players: null,
              role_order: 2,
              is_active: 1
            },
            'injury_backup': {
              id: null,
              team_id: teamId,
              role: 'Injury Backup',
              pg_players: null,
              sg_players: null,
              sf_players: null,
              pf_players: null,
              c_players: null,
              role_order: 3,
              is_active: 1
            },
            'injury_backup_2': {
              id: null,
              team_id: teamId,
              role: 'Injury Backup 2',
              pg_players: null,
              sg_players: null,
              sf_players: null,
              pf_players: null,
              c_players: null,
              role_order: 4,
              is_active: 1
            },
            'injury_backup_3': {
              id: null,
              team_id: teamId,
              role: 'Injury Backup 3',
              pg_players: null,
              sg_players: null,
              sf_players: null,
              pf_players: null,
              c_players: null,
              role_order: 5,
              is_active: 1
            },
            'in_out_rotation': {
              id: null,
              team_id: teamId,
              role: 'In & Out of Rotation',
              pg_players: null,
              sg_players: null,
              sf_players: null,
              pf_players: null,
              c_players: null,
              role_order: 6,
              is_active: 1
            },
            'out_rotation': {
              id: null,
              team_id: teamId,
              role: 'Out of Rotation',
              pg_players: null,
              sg_players: null,
              sf_players: null,
              pf_players: null,
              c_players: null,
              role_order: 7,
              is_active: 1
            }
          }
          
          this.depthChartData = initialDepthChartData
          
          // Save the initial structure to the API
          await this.saveDepthChartData()
          
        } else if (Array.isArray(response.data)) {
          // Transform the API response to match our component structure
          const transformedData = {}
          
          response.data.forEach(roleData => {
            const role = roleData.role.toLowerCase().replace(/\s+/g, '_')
            
            // Map role names to our component format
            const roleMapping = {
              'starter': 'starter',
              'rotational_player': 'rotational',
              'injury_backup': 'injury_backup',
              'injury_backup_2': 'injury_backup_2',
              'injury_backup_3': 'injury_backup_3',
              'in_&_out_of_rotation': 'in_out_rotation',
              'out_of_rotation': 'out_rotation'
            }
            
            const mappedRole = roleMapping[role] || role
            
            transformedData[mappedRole] = {
              id: roleData.id,
              team_id: roleData.team_id,
              role: roleData.role,
              pg_players: roleData.pg_players,
              sg_players: roleData.sg_players,
              sf_players: roleData.sf_players,
              pf_players: roleData.pf_players,
              c_players: roleData.c_players,
              role_order: roleData.role_order,
              is_active: roleData.is_active
            }
          })
          
          this.depthChartData = transformedData
        } else {
          console.warn('Unexpected depth chart API response format:', response.data)
          this.depthChartData = null
        }
      } catch (error) {
        console.error('Error fetching depth chart data:', error)
        // Show user-friendly error message
        if (error.response?.status === 404) {
          console.log('No depth chart data found for this team')
          // Create initial structure even on 404
          await this.createInitialDepthChartStructure(teamId)
        } else {
          console.error('Failed to fetch depth chart data:', error.message)
        }
        this.depthChartData = null
      } finally {
        this.isLoadingDepthChart = false
      }
    },
    
    // Helper method to create initial depth chart structure
    async createInitialDepthChartStructure(teamId) {
      const initialDepthChartData = {
        'starter': {
          id: null,
          team_id: teamId,
          role: 'Starter',
          pg_players: null,
          sg_players: null,
          sf_players: null,
          pf_players: null,
          c_players: null,
          role_order: 1,
          is_active: 1
        },
        'rotational': {
          id: null,
          team_id: teamId,
          role: 'Rotational Player',
          pg_players: null,
          sg_players: null,
          sf_players: null,
          pf_players: null,
          c_players: null,
          role_order: 2,
          is_active: 1
        },
        'injury_backup': {
          id: null,
          team_id: teamId,
          role: 'Injury Backup',
          pg_players: null,
          sg_players: null,
          sf_players: null,
          pf_players: null,
          c_players: null,
          role_order: 3,
          is_active: 1
        },
        'injury_backup_2': {
          id: null,
          team_id: teamId,
          role: 'Injury Backup 2',
          pg_players: null,
          sg_players: null,
          sf_players: null,
          pf_players: null,
          c_players: null,
          role_order: 4,
          is_active: 1
        },
        'injury_backup_3': {
          id: null,
          team_id: teamId,
          role: 'Injury Backup 3',
          pg_players: null,
          sg_players: null,
          sf_players: null,
          pf_players: null,
          c_players: null,
          role_order: 5,
          is_active: 1
        },
        'in_out_rotation': {
          id: null,
          team_id: teamId,
          role: 'In & Out of Rotation',
          pg_players: null,
          sg_players: null,
          sf_players: null,
          pf_players: null,
          c_players: null,
          role_order: 6,
          is_active: 1
        },
        'out_rotation': {
          id: null,
          team_id: teamId,
          role: 'Out of Rotation',
          pg_players: null,
          sg_players: null,
          sf_players: null,
          pf_players: null,
          c_players: null,
          role_order: 7,
          is_active: 1
        }
      }
      
      this.depthChartData = initialDepthChartData
      
      // Save the initial structure to the API
      await this.saveDepthChartData()
    },
    
    // Depth chart helper method
    getDepthChartPlayer(position, role) {
      if (!this.depthChartData || !this.depthChartData[role]) {
        return null
      }
      
      // Map position to the correct field name
      const positionField = `${position.toLowerCase()}_players`
      const playerId = this.depthChartData[role][positionField]
      
      if (!playerId) {
        return null
      }
      
      console.log(`Looking for player ID: ${playerId} in position: ${position}, role: ${role}`)
      
      // Find the player name from the team roster
      if (this.selectedTeam && this.selectedTeam.roster) {
        console.log('Team roster:', this.selectedTeam.roster)
        
        const rosterItem = this.selectedTeam.roster.find(item => {
          // Check multiple possible ID fields
          const itemId = item.id
          const playerId1 = item.player?.p_player_id
          const playerId2 = item.player?.player_id
          const playerId3 = item.player?.id
          
          console.log(`Comparing: ${playerId} with item.id: ${itemId}, p_player_id: ${playerId1}, player_id: ${playerId2}, player.id: ${playerId3}`)
          
          return itemId == playerId || 
                 playerId1 == playerId || 
                 playerId2 == playerId || 
                 playerId3 == playerId
        })
        
        if (rosterItem && rosterItem.player) {
          console.log(`Found player: ${rosterItem.player.player_name}`)
          return rosterItem.player.player_name
        }
        
        console.log('Player not found in roster')
      }
      
      // Fallback to player ID if name not found
      console.log(`Using fallback: Player ${playerId}`)
      return `Player ${playerId}`
    },
    
    // Get player status for depth chart position
    getDepthChartPlayerStatus(position, role) {
      if (!this.depthChartData || !this.depthChartData[role]) {
        return null
      }
      
      // Map position to the correct field name
      const positionField = `${position.toLowerCase()}_players`
      const playerId = this.depthChartData[role][positionField]
      
      if (!playerId) {
        return null
      }
      
      // Find the player status from the team roster
      if (this.selectedTeam && this.selectedTeam.roster) {
        const rosterItem = this.selectedTeam.roster.find(item => {
          const itemId = item.id
          const playerId1 = item.player?.p_player_id
          const playerId2 = item.player?.player_id
          const playerId3 = item.player?.id
          
          return itemId == playerId || 
                 playerId1 == playerId || 
                 playerId2 == playerId || 
                 playerId3 == playerId
        })
        
        if (rosterItem && rosterItem.player) {
          // Prioritize the new player_team_status.player_status_id (same as PlayerStatusDropdown uses)
          // Only fallback to old player_status when new version is missing
          if (rosterItem.player.player_team_status?.player_status_id !== undefined && rosterItem.player.player_team_status?.player_status_id !== null) {
            return rosterItem.player.player_team_status.player_status_id
          }
          // Fallback to old version only when new version is missing
          if (rosterItem.player.player_status !== undefined && rosterItem.player.player_status !== null) {
            return rosterItem.player.player_status
          }
          return null
        }
      }
      
      return null
    },
    
    // Get status letter: 1=A (Active), 2=Q (Questionable), 3=O (Out), 4=P (Probable), 5=D (Doubtful)
    getStatusLetter(status) {
      if (!status) return ''
      
      const statusId = status?.id || status
      
      if (statusId === 1) return 'A' // Active
      if (statusId === 2) return 'Q' // Questionable
      if (statusId === 3) return 'O' // Out
      if (statusId === 4) return 'P' // Probable
      if (statusId === 5) return 'D' // Doubtful
      
      return ''
    },
    
    // Get status badge color class: 1=Active (green), 2=Questionable (orange), 3=Out (red), 4=Probable (light-green), 5=Doubtful (yellow)
    getStatusBadgeClass(status) {
      if (!status) return 'bg-gray-200 text-gray-800'
      
      const statusId = status?.id || status
      
      if (statusId === 1) return 'bg-green-600 text-white' // Active
      if (statusId === 2) return 'bg-orange-500 text-white' // Questionable
      if (statusId === 3) return 'bg-red-600 text-white' // Out
      if (statusId === 4) return 'bg-green-400 text-white' // Probable
      if (statusId === 5) return 'bg-yellow-500 text-white' // Doubtful
      
      return 'bg-gray-200 text-gray-800'
    },
    async openPlayerProfile(player) {
      console.log('Opening player profile:', player)
      this.selectedPlayer = player
      this.selectedProfile = null // Clear any selected profile
      // Find the index of the selected player in search results
      this.currentPlayerIndex = this.searchResults.findIndex(p => p.id === player.id)
      
      // Fetch detailed player data from API
      this.isLoadingPlayer = true
      try {
        const response = await axios.get(`/api/v2/player/${player.player_id}`)
        console.log('Detailed player data:', response.data)
        
        // Process the API response to extract team information from roster
        if (response.data) {
          let extractedTeam = response.data.team;
          let extractedTeams = [];
          
          // Extract team information from roster if available
          if (response.data.roster && response.data.roster.length > 0) {
            extractedTeams = response.data.roster.map(rosterItem => rosterItem.team?.team_name).filter(Boolean);
            if (extractedTeams.length > 0) {
              extractedTeam = extractedTeams[0]; // Use first team as primary team
            }
          }
          
          // Also extract additional team info for display
          const rosterTeams = response.data.roster?.map(rosterItem => ({
            name: rosterItem.team?.team_name,
            id: rosterItem.team?.id,
            league: rosterItem.team?.league
          })).filter(team => team.name) || [];
          
          this.selectedPlayer = {
            ...response.data,
            // Use extracted team data or fallbacks
            team: extractedTeam || 'Team information not available',
            position: response.data.position || 'Position not specified',
            nationality: response.data.nationality || 'Nationality not available',
            birthdate: response.data.birthdate || 'Birth date not available',
            height: response.data.height_cm ? `${response.data.height_cm}cm` : 'Height not available',
            age: response.data.age || 'Age not available',
            // Store extracted teams for the PlayerProfile component
            extractedTeams: extractedTeams
          }
        } else {
          this.selectedPlayer = response.data
        }
      } catch (error) {
        console.error('Error fetching player details:', error)
        // Keep the original player data if API call fails
      } finally {
        this.isLoadingPlayer = false
      }
    },
    addPlayerProfile(player) {
      console.log('Adding profile for player:', player)
      
      // Create a new profile for the selected player
      const newProfile = {
        id: Date.now(), // Temporary ID
        name: player.player_name,
        description: `Profile for ${player.player_name}${player.team ? ` (${player.team})` : ''}`,
        lastUpdated: 'Just now',
        status: 'active',
        playerData: player, // Store the original player data
        activeTeams: [
          player.team || 'Kalev/Cramo',
          player.nationalTeam || 'Estonian National Basketball Team'
        ].filter(team => team),
        playerLabels: [
          'Multi Year Returning Player',
          'Returning Player'
        ],
        settings: {
          scoringWeight: 40,
          assistWeight: 30,
          reboundWeight: 30,
          historicalPeriod: 90,
          confidenceLevel: 'medium',
          includeInjuries: true,
          includeForm: true,
          includeMatchups: false
        }
      }
      
      // Add to the beginning of the profiles list
      this.playerProfiles.unshift(newProfile)
      
      // Select the new profile
      this.selectedProfile = newProfile
      
      // Clear the search
      this.clearPlayerSearch()
      
      // You could also save this to your backend here
    },
    selectProfile(profile) {
      this.selectedProfile = profile
      
      // Initialize settings if they don't exist
      if (!profile.settings) {
        profile.settings = {
          scoringWeight: 40,
          assistWeight: 30,
          reboundWeight: 30,
          historicalPeriod: 90,
          confidenceLevel: 'medium',
          includeInjuries: true,
          includeForm: true,
          includeMatchups: false
        }
      }
      
      // Initialize activeTeams if they don't exist
      if (!profile.activeTeams) {
        profile.activeTeams = [
          profile.playerData?.team || 'Kalev/Cramo',
          profile.playerData?.nationalTeam || 'Estonian National Basketball Team'
        ].filter(team => team)
      }
      
      // Initialize playerLabels if they don't exist
      if (!profile.playerLabels) {
        profile.playerLabels = [
          'Multi Year Returning Player',
          'Returning Player'
        ]
      }
    },
    saveProfileSettings() {
      // Update the last updated time
      this.selectedProfile.lastUpdated = 'Just now'
      
      // Here you would typically save to your backend
      console.log('Saving profile settings:', this.selectedProfile)
      
      // You could make an API call here to save the settings
      // await axios.post('/api/profiles/update', this.selectedProfile)
    },
    resetProfileSettings() {
      // Reset to default settings
      this.selectedProfile.settings = {
        scoringWeight: 40,
        assistWeight: 30,
        reboundWeight: 30,
        historicalPeriod: 90,
        confidenceLevel: 'medium',
        includeInjuries: true,
        includeForm: true,
        includeMatchups: false
      }
    },
    
    // Team functions
    addTeam() {
      this.newTeam = ' '
    },
    
    saveTeam() {
      if (this.newTeam.trim()) {
        this.selectedProfile.activeTeams.push(this.newTeam.trim())
        this.newTeam = ''
      }
    },
    
    removeTeam(index) {
      this.selectedProfile.activeTeams.splice(index, 1)
    },
    
    startEditTeam(index) {
      this.editingTeam = index
    },
    
    saveEditTeam(index) {
      this.editingTeam = null
    },
    
    cancelEditTeam() {
      this.editingTeam = null
    },
    
    // Label functions
    addLabel() {
      this.newLabel = ' '
    },
    
    saveLabel() {
      if (this.newLabel.trim()) {
        this.selectedProfile.playerLabels.push(this.newLabel.trim())
        this.newLabel = ''
      }
    },
    
    removeLabel(index) {
      this.selectedProfile.playerLabels.splice(index, 1)
    },
    
    startEditLabel(index) {
      this.editingLabel = index
    },
    
    saveEditLabel(index) {
      this.editingLabel = null
    },
    
    cancelEditLabel() {
      this.editingLabel = null
    },
    
    postPlayerProfile() {
      console.log('Posting player profile:', this.selectedProfile)
      // Here you would implement the logic to post the player profile
      // This could involve:
      // 1. Saving the profile to your backend
      // 2. Publishing it to a feed or dashboard
      // 3. Sending notifications
      // 4. Updating the profile status
      
      // Example implementation:
      // await axios.post('/api/profiles/publish', this.selectedProfile)
      
      // For now, just log the action
      alert('Player profile posted successfully!')
    },
    
    async previousPlayer() {
      if (this.currentPlayerIndex > 0) {
        this.currentPlayerIndex--
        const player = this.searchResults[this.currentPlayerIndex]
        this.selectedPlayer = player
        
        // Fetch detailed player data from API
        this.isLoadingPlayer = true
        try {
          const response = await axios.get(`/api/player/${player.player_id}`)
          console.log('Detailed player data:', response.data)
          
          // Process the API response to extract team information from roster
          if (response.data) {
            let extractedTeam = response.data.team;
            let extractedTeams = [];
            
            // Extract team information from roster if available
            if (response.data.roster && response.data.roster.length > 0) {
              extractedTeams = response.data.roster.map(rosterItem => rosterItem.team?.team_name).filter(Boolean);
              if (extractedTeams.length > 0) {
                extractedTeam = extractedTeams[0]; // Use first team as primary team
              }
            }
            
            this.selectedPlayer = {
              ...response.data,
              // Use extracted team data or fallbacks
              team: extractedTeam || 'Team information not available',
              position: response.data.position || 'Position not specified',
              nationality: response.data.nationality || 'Nationality not available',
              birthdate: response.data.birthdate || 'Birth date not available',
              height: response.data.height_cm ? `${response.data.height_cm}cm` : 'Height not available',
              age: response.data.age || 'Age not available',
              // Store extracted teams for the PlayerProfile component
              extractedTeams: extractedTeams
            }
          } else {
            this.selectedPlayer = response.data
          }
        } catch (error) {
          console.error('Error fetching player details:', error)
          // Keep the original player data if API call fails
        } finally {
          this.isLoadingPlayer = false
        }
      }
    },
    
    async nextPlayer() {
      if (this.currentPlayerIndex < this.searchResults.length - 1) {
        this.currentPlayerIndex++
        const player = this.searchResults[this.currentPlayerIndex]
        this.selectedPlayer = player
        
        // Fetch detailed player data from API
        this.isLoadingPlayer = true
        try {
          const response = await axios.get(`/api/player/${player.player_id}`)
          console.log('Detailed player data:', response.data)
          
          // Process the API response to extract team information from roster
          if (response.data) {
            let extractedTeam = response.data.team;
            let extractedTeams = [];
            
            // Extract team information from roster if available
            if (response.data.roster && response.data.roster.length > 0) {
              extractedTeams = response.data.roster.map(rosterItem => rosterItem.team?.team_name).filter(Boolean);
              if (extractedTeams.length > 0) {
                extractedTeam = extractedTeams[0]; // Use first team as primary team
              }
            }
            
            this.selectedPlayer = {
              ...response.data,
              // Use extracted team data or fallbacks
              team: extractedTeam || 'Team information not available',
              position: response.data.position || 'Position not specified',
              nationality: response.data.nationality || 'Nationality not available',
              birthdate: response.data.birthdate || 'Birth date not available',
              height: response.data.height_cm ? `${response.data.height_cm}cm` : 'Height not available',
              age: response.data.age || 'Age not available',
              // Store extracted teams for the PlayerProfile component
              extractedTeams: extractedTeams
            }
          } else {
            this.selectedPlayer = response.data
          }
        } catch (error) {
          console.error('Error fetching player details:', error)
          // Keep the original player data if API call fails
        } finally {
          this.isLoadingPlayer = false
        }
      }
    },
    
    backToSearch() {
      this.selectedPlayer = null
      this.currentPlayerIndex = 0
    },
    
    // Label management methods
    async searchLabels() {
      // Clear previous timeout
      if (this.labelSearchTimeout) {
        clearTimeout(this.labelSearchTimeout)
      }

      // Set new timeout for debounced search
      this.labelSearchTimeout = setTimeout(async () => {
        if (this.labelSearch.length < 2) {
          this.labelResults = []
          return
        }

        this.isSearchingLabels = true
        try {
          // You would implement the actual API call here
          // const response = await axios.get(`/api/labels/search/${encodeURIComponent(this.labelSearch)}`)
          // this.labelResults = response.data
          
          // For now, using mock data
          this.labelResults = [
            {
              id: 1,
              name: 'Multi Year Returning Player',
              category: 'Experience',
              description: 'Players who have been with the team for multiple years'
            },
            {
              id: 2,
              name: 'Returning Player',
              category: 'Experience',
              description: 'Players returning from previous season'
            },
            {
              id: 3,
              name: 'Rookie',
              category: 'Experience',
              description: 'First year players'
            }
          ].filter(label => 
            label.name.toLowerCase().includes(this.labelSearch.toLowerCase()) ||
            label.category.toLowerCase().includes(this.labelSearch.toLowerCase())
          )
        } catch (error) {
          console.error('Error searching labels:', error)
          this.labelResults = []
        } finally {
          this.isSearchingLabels = false
        }
      }, 300) // 300ms delay
    },
    
    clearLabelSearch() {
      this.labelSearch = ''
      this.labelResults = []
    },
    
    openLabelDetails(label) {
      console.log('Opening label details:', label)
      this.selectedLabel = label
      this.currentLabelIndex = this.labelResults.findIndex(l => l.id === label.id)
    },
    
    previousLabel() {
      if (this.currentLabelIndex > 0) {
        this.currentLabelIndex--
        this.selectedLabel = this.labelResults[this.currentLabelIndex]
      }
    },
    
    nextLabel() {
      if (this.currentLabelIndex < this.labelResults.length - 1) {
        this.currentLabelIndex++
        this.selectedLabel = this.labelResults[this.currentLabelIndex]
      }
    },
    
    saveLabelSettings() {
      console.log('Saving label settings:', this.selectedLabel)
      // Implement save logic here
      alert('Label saved successfully!')
    },
    
    deleteLabel() {
      if (confirm('Are you sure you want to delete this label?')) {
        console.log('Deleting label:', this.selectedLabel)
        // Implement delete logic here
        this.selectedLabel = null
        this.labelResults = this.labelResults.filter(l => l.id !== this.selectedLabel?.id)
        alert('Label deleted successfully!')
      }
    },
    
    // Team management methods
    async searchTeams() {
      // Clear previous timeout
      if (this.teamSearchTimeout) {
        clearTimeout(this.teamSearchTimeout)
      }

      // Set new timeout for debounced search
      this.teamSearchTimeout = setTimeout(async () => {
        if (this.teamSearch.length < 2) {
          this.teamResults = []
          return
        }

        this.isSearchingTeams = true
        try {
          // Make actual API call to search teams
          const response = await axios.get(`/api/v2/teams/search/${encodeURIComponent(this.teamSearch)}`)
          console.log('Team search results:', response.data)
          this.teamResults = response.data
        } catch (error) {
          console.error('Error searching teams:', error)
          this.teamResults = []
        } finally {
          this.isSearchingTeams = false
        }
      }, 300) // 300ms delay
    },
    
    clearTeamSearch() {
      this.teamSearch = ''
      this.teamResults = []
    },
    
    async openTeamDetails(team) {
      console.log('Opening team details:', team)
      this.selectedTeam = team
      this.currentTeamIndex = this.teamResults.findIndex(t => t.id === team.id)
      
      // Fetch detailed team data from API
      this.isLoadingTeam = true
      try {
        const response = await axios.get(`/api/v2/team/${team.id}`)
        console.log('Detailed team data:', response.data)
        
        // Update the selected team with detailed information
        this.selectedTeam = {
          ...team,
          ...response.data
        }

        // Initialize player_team_status for all roster items if missing
        if (this.selectedTeam.roster && Array.isArray(this.selectedTeam.roster)) {
          this.selectedTeam.roster.forEach(rosterItem => {
            if (rosterItem.player && !rosterItem.player.player_team_status) {
              rosterItem.player.player_team_status = {
                player_status_id: rosterItem.player.player_status?.id || rosterItem.player.player_status || 1
              }
            }
          })
        }

        // Fetch depth chart data for the selected team
        await this.fetchDepthChartData(team.id)
        
        // Automatically fetch last 3 games for all players
        // Use nextTick to ensure roster is fully rendered
        await this.$nextTick()
        await this.fetchLastThreeGamesForAllPlayers()
      } catch (error) {
        console.error('Error fetching team details:', error)
        // Keep the original team data if API call fails
      } finally {
        this.isLoadingTeam = false
      }
    },
    
    async previousTeam() {
      if (this.currentTeamIndex > 0) {
        this.currentTeamIndex--
        const team = this.teamResults[this.currentTeamIndex]
        this.selectedTeam = team
        
        // Fetch detailed team data from API
        this.isLoadingTeam = true
        try {
          const response = await axios.get(`/api/v2/team/${team.id}`)
          console.log('Detailed team data:', response.data)
          
          // Update the selected team with detailed information
          this.selectedTeam = {
            ...team,
            ...response.data
          }

          // Initialize player_team_status for all roster items if missing
          if (this.selectedTeam.roster && Array.isArray(this.selectedTeam.roster)) {
            this.selectedTeam.roster.forEach(rosterItem => {
              if (rosterItem.player && !rosterItem.player.player_team_status) {
                rosterItem.player.player_team_status = {
                  player_status_id: rosterItem.player.player_status?.id || rosterItem.player.player_status || 1
                }
              }
            })
          }
          
          // Fetch depth chart data for the selected team
          await this.fetchDepthChartData(team.id)
          
          // Automatically fetch last 3 games for all players
          // Use nextTick to ensure roster is fully rendered
          await this.$nextTick()
          await this.fetchLastThreeGamesForAllPlayers()
        } catch (error) {
          console.error('Error fetching team details:', error)
          // Keep the original team data if API call fails
        } finally {
          this.isLoadingTeam = false
        }
      }
    },
    
    async nextTeam() {
      if (this.currentTeamIndex < this.teamResults.length - 1) {
        this.currentTeamIndex++
        const team = this.teamResults[this.currentTeamIndex]
        this.selectedTeam = team
        
        // Fetch detailed team data from API
        this.isLoadingTeam = true
        try {
          const response = await axios.get(`/api/v2/team/${team.id}`)
          console.log('Detailed team data:', response.data)
          
          // Update the selected team with detailed information
          this.selectedTeam = {
            ...team,
            ...response.data
          }

          // Initialize player_team_status for all roster items if missing
          if (this.selectedTeam.roster && Array.isArray(this.selectedTeam.roster)) {
            this.selectedTeam.roster.forEach(rosterItem => {
              if (rosterItem.player && !rosterItem.player.player_team_status) {
                rosterItem.player.player_team_status = {
                  player_status_id: rosterItem.player.player_status?.id || rosterItem.player.player_status || 1
                }
              }
            })
          }
          
          // Fetch depth chart data for the selected team
          await this.fetchDepthChartData(team.id)
          
          // Automatically fetch last 3 games for all players
          // Use nextTick to ensure roster is fully rendered
          await this.$nextTick()
          await this.fetchLastThreeGamesForAllPlayers()
        } catch (error) {
          console.error('Error fetching team details:', error)
          // Keep the original team data if API call fails
        } finally {
          this.isLoadingTeam = false
        }
      }
    },
    
    saveTeamSettings() {
      console.log('Saving team settings:', this.selectedTeam)
      // Implement save logic here
      alert('Team saved successfully!')
    },
    
    clearTeamSelection() {
      this.selectedTeam = null
      this.depthChartData = null
    },
    
    deleteTeam() {
      if (confirm('Are you sure you want to delete this team?')) {
        console.log('Deleting team:', this.selectedTeam)
        // Implement delete logic here
        this.selectedTeam = null
        this.depthChartData = null
        this.teamResults = this.teamResults.filter(t => t.id !== this.selectedTeam?.id)
        alert('Team deleted successfully!')
      }
    },
    
    // Player Labels Table Management Methods
         async saveWeights() {
       this.isSaving = true
       try {
         // Transform component data back to API format based on current menu
         const apiData = this.playerLabelMenu === 'projection_weights' 
           ? this.transformComponentDataToApiFormat(this.labelProjectionData)
           : this.transformMinutesDataToApiFormat(this.labelProjectionData)
         
         console.log(`Saving ${this.playerLabelMenu} weights:`, apiData)
         
         // Use playerLabelMenu directly for endpoint and type
         const endpoint = `/api/v2/projections-settings/player-labels/${this.playerLabelMenu}`
         
         // Save to API with type field
         const response = await axios.post(endpoint, apiData)
         
         console.log('Save response:', response.data)
         alert(`${this.playerLabelMenu === 'projection_weights' ? 'Projection' : 'Minutes Settings'} weights saved successfully!`)
         
         // Refresh data from server
         await this.fetchProjectionLabels()
         
       } catch (error) {
         console.error('Error saving weights:', error)
         alert('Error saving weights. Please try again.')
       } finally {
         this.isSaving = false
       }
     },
     

     
     transformMinutesDataToApiFormat(componentData) {
       const apiData = []
       
       componentData.forEach(category => {
         // Add "0" minutes row
         apiData.push({
           type: 'minutes_settings_weights',
           label: category.name,
           slug: category.slug || this.generateSlug(category.name),
           games_range: '0',
           last_season_weight: this.getMinutesData(category, 0, 'lastSeason'),
           two_seasons_ago_weight: this.getMinutesData(category, 0, 'twoSeasonsAgo'),
           three_seasons_ago_weight: this.getMinutesData(category, 0, 'threeSeasonsAgo'),
           league_avg_weight: this.getMinutesData(category, 0, 'leagueAvg'),
           last_5_games_weight: this.getMinutesData(category, 0, 'lastFiveGames'),
           rest_of_curr_season_weight: this.getMinutesData(category, 0, 'restOfSeason'),
           is_active: 1,
           sort_order: apiData.length + 1
         })
         
         // Add game range rows
         category.rows.forEach(row => {
           apiData.push({
             type: 'minutes_settings_weights',
             label: category.name,
             slug: category.slug || this.generateSlug(category.name),
             games_range: row.games,
             last_season_weight: row.lastSeason,
             two_seasons_ago_weight: row.twoSeasonsAgo,
             three_seasons_ago_weight: row.threeSeasonsAgo,
             league_avg_weight: row.leagueAvg,
             last_5_games_weight: row.lastFiveGames,
             rest_of_curr_season_weight: row.restOfSeason,
             is_active: 1,
             sort_order: apiData.length + 1
           })
         })
       })
       
       return apiData
     },
     
     transformComponentDataToApiFormat(componentData) {
       const apiData = []
       
       componentData.forEach(category => {
         category.rows.forEach(row => {
           apiData.push({
             type: 'projection_weights',
             label: category.name,
             slug: category.slug || this.generateSlug(category.name),
             games_range: row.games,
             last_season_weight: row.lastSeason,
             two_seasons_ago_weight: row.twoSeasonsAgo,
             three_seasons_ago_weight: row.threeSeasonsAgo,
             league_avg_weight: row.leagueAvg,
             last_5_games_weight: row.lastFiveGames,
             rest_of_curr_season_weight: row.restOfSeason,
             is_active: 1,
             sort_order: apiData.length + 1
           })
         })
       })
       
       return apiData
     },
     
     generateSlug(name) {
       return name.toLowerCase()
         .replace(/[^a-z0-9]+/g, '-')
         .replace(/(^-|-$)/g, '')
     },
    
    resetToDefaults() {
      if (confirm('Are you sure you want to reset all projection weights to default values?')) {
        // Reset to default values
        this.labelProjectionData = [
          {
            name: 'Returning',
            rows: [
              { games: '0-5 Games', lastSeason: 0.75, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.25, restOfSeason: 0 },
              { games: '6-10 Games', lastSeason: 0.6, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.3, restOfSeason: 0.1 },
              { games: '11-15 Games', lastSeason: 0.45, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.35, restOfSeason: 0.2 },
              { games: '16-20 Games', lastSeason: 0.3, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.4, restOfSeason: 0.3 },
              { games: '21+ Games', lastSeason: 0.1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.5, restOfSeason: 0.4 }
            ]
          },
          {
            name: 'Multi-Year Returning',
            rows: [
              { games: '0-5 Games', lastSeason: 0.6, twoSeasonsAgo: 0.25, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.15, restOfSeason: 0 },
              { games: '6-10 Games', lastSeason: 0.5, twoSeasonsAgo: 0.2, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.2, restOfSeason: 0.1 },
              { games: '11-15 Games', lastSeason: 0.4, twoSeasonsAgo: 0.15, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.25, restOfSeason: 0.2 },
              { games: '16-20 Games', lastSeason: 0.3, twoSeasonsAgo: 0.1, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.3, restOfSeason: 0.3 },
              { games: '21+ Games', lastSeason: 0.2, twoSeasonsAgo: 0.05, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0.4, restOfSeason: 0.35 }
            ]
          },
          {
            name: 'Same League (New Team)',
            rows: [
              { games: '0-5 Games', lastSeason: 0.7, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.1, lastFiveGames: 0.2, restOfSeason: 0 },
              { games: '6-10 Games', lastSeason: 0.55, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.15, lastFiveGames: 0.25, restOfSeason: 0.05 },
              { games: '11-15 Games', lastSeason: 0.4, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.2, lastFiveGames: 0.3, restOfSeason: 0.1 },
              { games: '16-20 Games', lastSeason: 0.25, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.25, lastFiveGames: 0.35, restOfSeason: 0.15 },
              { games: '21+ Games', lastSeason: 0.1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.3, lastFiveGames: 0.4, restOfSeason: 0.2 }
            ]
          },
          {
            name: 'from Higher Comp',
            rows: [
              { games: '0-5 Games', lastSeason: 0.8, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.1, lastFiveGames: 0.1, restOfSeason: 0 },
              { games: '6-10 Games', lastSeason: 0.65, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.15, lastFiveGames: 0.15, restOfSeason: 0.05 },
              { games: '11-15 Games', lastSeason: 0.5, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.2, lastFiveGames: 0.2, restOfSeason: 0.1 },
              { games: '16-20 Games', lastSeason: 0.35, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.25, lastFiveGames: 0.25, restOfSeason: 0.15 },
              { games: '21+ Games', lastSeason: 0.2, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.3, lastFiveGames: 0.3, restOfSeason: 0.2 }
            ]
          },
          {
            name: 'from Lower Comp',
            rows: [
              { games: '0-5 Games', lastSeason: 0.3, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.5, lastFiveGames: 0.2, restOfSeason: 0 },
              { games: '6-10 Games', lastSeason: 0.25, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.55, lastFiveGames: 0.25, restOfSeason: 0.05 },
              { games: '11-15 Games', lastSeason: 0.2, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.6, lastFiveGames: 0.3, restOfSeason: 0.1 },
              { games: '16-20 Games', lastSeason: 0.15, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.65, lastFiveGames: 0.35, restOfSeason: 0.15 },
              { games: '21+ Games', lastSeason: 0.1, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.7, lastFiveGames: 0.4, restOfSeason: 0.2 }
            ]
          },
          {
            name: 'Rookie',
            rows: [
              { games: '0-5 Games', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.7, lastFiveGames: 0.3, restOfSeason: 0 },
              { games: '6-10 Games', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.65, lastFiveGames: 0.35, restOfSeason: 0.05 },
              { games: '11-15 Games', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.6, lastFiveGames: 0.4, restOfSeason: 0.1 },
              { games: '16-20 Games', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.55, lastFiveGames: 0.45, restOfSeason: 0.15 },
              { games: '21+ Games', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0.5, lastFiveGames: 0.5, restOfSeason: 0.2 }
            ]
          },
          {
            name: 'Manual',
            rows: [
              { games: '0-5 Games', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
              { games: '6-10 Games', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
              { games: '11-15 Games', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
              { games: '16-20 Games', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
              { games: '21+ Games', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 }
            ]
          }
        ]
        alert('Projection weights reset to defaults!')
      }
    },
    
         exportData() {
       // Create CSV content
       let csvContent = 'Label,Games,Last Season,2 Seasons ago,3 Seasons ago,League Avg,Last 5 games,Rest of Curr Season\n'
       
       this.labelProjectionData.forEach(category => {
         category.rows.forEach(row => {
           csvContent += `${category.name},${row.games},${row.lastSeason},${row.twoSeasonsAgo},${row.threeSeasonsAgo},${row.leagueAvg},${row.lastFiveGames},${row.restOfSeason}\n`
         })
       })
       
       // Create and download file
       const blob = new Blob([csvContent], { type: 'text/csv' })
       const url = window.URL.createObjectURL(blob)
       const a = document.createElement('a')
       a.href = url
       a.download = 'projection-weights.csv'
       document.body.appendChild(a)
       a.click()
       document.body.removeChild(a)
       window.URL.revokeObjectURL(url)
       
       alert('Data exported successfully!')
     },
     
     addNewLabel() {
       const newLabel = {
         name: 'New Label',
         isEditing: true,
         rows: [
           { games: '0', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
           { games: '1-5', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
           { games: '6-10', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
           { games: '11-15', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
           { games: '16-20', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 },
           { games: '21+', lastSeason: 0, twoSeasonsAgo: 0, threeSeasonsAgo: 0, leagueAvg: 0, lastFiveGames: 0, restOfSeason: 0 }
         ]
       }
       this.labelProjectionData.push(newLabel)
     },
     

     
     updateLabelName(categoryIndex, newName) {
       this.labelProjectionData[categoryIndex].name = newName
     },
     
     deleteLabel(categoryIndex) {
       const category = this.labelProjectionData[categoryIndex]
       this.labelProjectionData.splice(categoryIndex, 1)
       alert(`Label "${category.name}" deleted successfully!`)
     },
     
     async fetchProjectionLabels() {
       this.isLoadingLabels = true
       try {
         // Fetch labels based on current menu type using POST
         const response = await axios.post('/api/v2/projections-settings/player-labels/projection-settings-labels', {
           type: this.playerLabelMenu
         })
         console.log(`${this.playerLabelMenu} labels response:`, response.data)
         
         // Transform the API data to match our component structure
         const transformedData = this.transformApiDataToComponentFormat(response.data)
         this.labelProjectionData = transformedData
         
       } catch (error) {
         console.error(`Error fetching ${this.playerLabelMenu} labels:`, error)
         // Keep the default data if API fails
       } finally {
         this.isLoadingLabels = false
       }
     },
     
     transformApiDataToComponentFormat(apiData) {
       // Group the API data by label
       const groupedData = {}
       
       apiData.forEach(item => {
         if (!groupedData[item.label]) {
           groupedData[item.label] = {
             name: item.label,
             slug: item.slug,
             rows: []
           }
         }
         
         // Map API fields to component fields
         const row = {
           games: item.games_range,
           lastSeason: parseFloat(item.last_season_weight) || 0,
           twoSeasonsAgo: parseFloat(item.two_seasons_ago_weight) || 0,
           threeSeasonsAgo: parseFloat(item.three_seasons_ago_weight) || 0,
           leagueAvg: parseFloat(item.league_avg_weight) || 0,
           lastFiveGames: parseFloat(item.last_5_games_weight) || 0,
           restOfSeason: parseFloat(item.rest_of_curr_season_weight) || 0
         }
         
         groupedData[item.label].rows.push(row)
       })
       
      // Sort rows by games_range for consistent display
      Object.values(groupedData).forEach(category => {
        console.log('Before sorting, category rows:', category.rows.map(r => r.games))
        
        category.rows.sort((a, b) => {
          // Define the desired order for game ranges (handle both formats)
          const gameRangeOrder = ['0', '1-5', '6-10', '11-15', '16-20', '21+']
          const gameRangeOrderWithGames = ['0', '1-5 Games', '6-10 Games', '11-15 Games', '16-20 Games', '21+ Games']
          
          // Try to find index in both possible formats
          let aIndex = gameRangeOrder.indexOf(a.games)
          if (aIndex === -1) {
            aIndex = gameRangeOrderWithGames.indexOf(a.games)
          }
          
          let bIndex = gameRangeOrder.indexOf(b.games)
          if (bIndex === -1) {
            bIndex = gameRangeOrderWithGames.indexOf(b.games)
          }
          
          console.log(`Comparing "${a.games}" (index: ${aIndex}) vs "${b.games}" (index: ${bIndex})`)
          
          // If both are in the order array, sort by their position
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex
          }
          
          // If only one is in the order array, prioritize it
          if (aIndex !== -1) return -1
          if (bIndex !== -1) return 1
          
          // If neither is in the order array, sort alphabetically
          return a.games.localeCompare(b.games)
        })
        
        console.log('After sorting, category rows:', category.rows.map(r => r.games))
      })
       
       // Convert to array format expected by component
       return Object.values(groupedData)
     },
     
     // Method to fetch data when menu changes
     async fetchDataForCurrentMenu() {
       if (this.activeTab === 'player_labels') {
         await this.fetchProjectionLabels()
       }
     },
    
    // Player Selection Methods for Depth Chart
    openPlayerSelection(position, role) {
      this.selectedPosition = position
      this.selectedRole = role
      this.showPlayerSelectionModal = true
    },
    
    selectPlayerForDepthChart(player) {
      if (!this.depthChartData || !this.depthChartData[this.selectedRole]) {
        console.error('Depth chart data or selected role not found')
        return
      }
      
      console.log('Selecting player for depth chart:', player)
      console.log('Selected position:', this.selectedPosition)
      console.log('Selected role:', this.selectedRole)
      
      // Map position to the correct field name
      const positionField = `${this.selectedPosition.toLowerCase()}_players`
      
      // Determine the correct player ID to store
      const playerId = player.id || player.p_player_id || player.player_id
      
      console.log(`Storing player ID: ${playerId} in field: ${positionField}`)
      
      // Set the player ID for the selected position and role
      this.depthChartData[this.selectedRole][positionField] = playerId
      
      console.log('Updated depth chart data:', this.depthChartData[this.selectedRole])
      
      // Close the modal
      this.showPlayerSelectionModal = false
      this.selectedPosition = null
      this.selectedRole = null
      
      // Save the updated depth chart data to the API
      this.saveDepthChartData()
    },
    
    async saveDepthChartData() {
      if (!this.selectedTeam || !this.depthChartData) {
        return
      }
      
      try {
        // Transform our data back to the API format
        const apiData = Object.values(this.depthChartData).map(roleData => ({
          id: roleData.id,
          team_id: this.selectedTeam.id,
          role: roleData.role,
          pg_players: roleData.pg_players,
          sg_players: roleData.sg_players,
          sf_players: roleData.sf_players,
          pf_players: roleData.pf_players,
          c_players: roleData.c_players,
          role_order: roleData.role_order || 1,
          is_active: roleData.is_active || 1
        }))
        
        // Check if this is initial creation (no IDs) or update (has IDs)
        const hasExistingIds = apiData.some(role => role.id !== null)
        
        let response
        if (hasExistingIds) {
          // Update existing depth chart
          response = await axios.put(`/api/v2/projections-settings/teams/depth-chart/${this.selectedTeam.id}`, apiData)
          console.log('Depth chart updated successfully:', response.data)
        } else {
          // Create new depth chart
          response = await axios.post(`/api/v2/projections-settings/teams/depth-chart/${this.selectedTeam.id}`, apiData)
          console.log('Depth chart created successfully:', response.data)
          
          // Update the local data with the returned IDs
          if (response.data && Array.isArray(response.data)) {
            response.data.forEach((createdRole, index) => {
              const roleKey = Object.keys(this.depthChartData)[index]
              if (roleKey && this.depthChartData[roleKey]) {
                this.depthChartData[roleKey].id = createdRole.id
              }
            })
          }
        }
      } catch (error) {
        console.error('Error saving depth chart:', error)
        alert('Failed to save depth chart. Please try again.')
      }
    },
    
    closePlayerSelectionModal() {
      this.showPlayerSelectionModal = false
      this.selectedPosition = null
      this.selectedRole = null
    },
    
    // Method to clear/remove a player from a specific cell
    clearPlayerFromCell(position, role) {
      if (!this.depthChartData || !this.depthChartData[role]) {
        console.error('Depth chart data or role not found')
        return
      }
      
      console.log(`Clearing player from position: ${position}, role: ${role}`)
      
      // Map position to the correct field name
      const positionField = `${position.toLowerCase()}_players`
      
      // Clear the player ID for the selected position and role
      this.depthChartData[role][positionField] = null
      
      console.log('Updated depth chart data:', this.depthChartData[role])
      
      // Save the updated depth chart data to the API
      this.saveDepthChartData()
    },
    
    autoFillDepthChart() {
      if (!this.selectedTeam || !this.selectedTeam.roster || !this.depthChartData) {
        return
      }
      
      // Filter players where shouldShowNo returns false (yes players only)
      const availablePlayers = this.selectedTeam.roster
        .filter(rosterItem => {
          if (!rosterItem.player) return false
          return !this.shouldShowNo(rosterItem)
        })
        .map(rosterItem => ({
          rosterItem: rosterItem,
          player: rosterItem.player,
          avgMinutes: rosterItem.player?.teamSeasonMinutes?.avg ?? 0,
          positions: (rosterItem.player?.position || '').split(',').map(p => p.trim().toUpperCase())
        }))
        .sort((a, b) => b.avgMinutes - a.avgMinutes) // Sort by average minutes descending
      
      if (availablePlayers.length === 0) {
        return
      }
      
      // Positions in order: C, PF, SF, SG, PG (from lowest to highest)
      const positionOrder = ['C', 'PF', 'SF', 'SG', 'PG']
      const roles = ['starter', 'rotational', 'in_out_rotation'] // First starter, then rotational, then in and out of rotation
      
      const usedPlayerIds = new Set()
      
      // Helper function to find upper positions (positions higher in the hierarchy)
      const getUpperPositions = (position) => {
        const positionIndex = positionOrder.indexOf(position)
        if (positionIndex === -1 || positionIndex === positionOrder.length - 1) {
          return [] // No upper positions for PG or invalid position
        }
        return positionOrder.slice(positionIndex + 1) // Return all positions above this one
      }
      
      // Helper function to find best player for a position, with fallback to upper positions
      const findBestPlayerForPosition = (position, role, usedIds) => {
        // First try exact position match
        let bestPlayer = availablePlayers.find(player => {
          if (usedIds.has(player.rosterItem.player_id)) return false
          return player.positions.includes(position)
        })
        
        if (bestPlayer) return bestPlayer
        
        // If no exact match, try upper positions (closest first)
        const upperPositions = getUpperPositions(position)
        for (const upperPos of upperPositions) {
          bestPlayer = availablePlayers.find(player => {
            console.log('player.rosterItem', player.rosterItem)
            if (usedIds.has(player.rosterItem.player_id)) return false
            return player.positions.includes(upperPos)
          })
          if (bestPlayer) return bestPlayer
        }
        
        return null
      }
      
      // First, clear all positions in all roles
      for (const role of roles) {
        if (!this.depthChartData[role]) {
          console.warn(`Role ${role} not found in depth chart data`)
          continue
        }
        
        // Clear all positions for this role
        for (const position of positionOrder) {
          const positionField = `${position.toLowerCase()}_players`
          this.depthChartData[role][positionField] = null
        }
      }
      
      // Now fill each role
      for (const role of roles) {
        if (!this.depthChartData[role]) {
          console.warn(`Role ${role} not found in depth chart data`)
          continue
        }
        
        // First pass: Fill each position in order with exact matches
        for (const position of positionOrder) {
          const positionField = `${position.toLowerCase()}_players`
          
          // Find the best available player for this position (exact match)
          const bestPlayer = availablePlayers.find(player => {
            if (usedPlayerIds.has(player.rosterItem.player_id)) return false
            return player.positions.includes(position)
          })
          
          if (bestPlayer) {
            this.depthChartData[role][positionField] = bestPlayer.rosterItem.player_id
            usedPlayerIds.add(bestPlayer.rosterItem.player_id)
            console.log(`Assigned ${bestPlayer.player.player_name} to ${position} (${role}) - exact match`)
          }
        }
        
        // Second pass: Fill any remaining empty positions with players from upper positions
        for (const position of positionOrder) {
          const positionField = `${position.toLowerCase()}_players`
          
          // Skip if already filled
          if (this.depthChartData[role][positionField]) {
            continue
          }
          
          // Try to find player from upper positions
          const bestPlayer = findBestPlayerForPosition(position, role, usedPlayerIds)
          
          if (bestPlayer) {
            this.depthChartData[role][positionField] = bestPlayer.rosterItem.player_id
            usedPlayerIds.add(bestPlayer.rosterItem.player_id)
            console.log(`Assigned ${bestPlayer.player.player_name} to ${position} (${role}) - from upper position`)
          }
        }
      }
      
      // Save the updated depth chart data
      this.saveDepthChartData()
    }
  }
}
</script>

<style scoped>
/* Add any custom styles here */
</style>
